import React, { createContext, useContext, useState, useEffect } from 'react';

export const LanguageContext = createContext();

const translations = {
  en: {
    // Nav & General
    overview: 'Overview',
    projects: 'Projects',
    manageProjects: 'Manage Projects',
    board: 'Board',
    uiImprovements: 'UI Improvements',
    signOut: 'Sign Out',
    searchPlaceholder: 'Search tickets, customers, settings...',
    noNotifications: 'No new notifications',
    profile: 'Profile',
    language: 'Language',
    inDevelopment: 'In Development',
    history: 'History',
    activeProject: 'Active Project',
    regressionTracking: 'MCP Tracking',
    regressionTrackingDesc: 'Monitoring of code fixes resolved by your AI agents.',
    liveMonitoring: 'Live Monitoring',
    regressionIntelligence: 'Regression Intelligence',
    onlyYourTickets: 'Personal Resolutions',
    agentId: 'Agent ID',
    resolvedByYou: 'Resolved by You',
    totalTicketsHandled: 'Total Resolved via MCP',
    pendingDeployment: 'Pending Deployment',
    liveToday: 'DEPLOYED TODAY',
    deployed: 'DEPLOYED',
    markAsLive: 'Mark as Deployed',
    appliedStatus: 'Applied (Local)',
    deploymentAction: 'Deploy to Review',
    ticketTimeline: 'Status Timeline',
    mcpEventLog: 'MCP Activity History',
    timelinePending: 'Ticket recorded in system',
    timelineLocked: 'IA PROCESSING',
    timelineApplied: 'APPLIED (LOCAL)',
    timelineReady: 'DEPLOYED',
    initializingTracking: 'INITIALIZING TRACKING...',
    aiProcessing: 'AI PROCESSING',
    loadingMore: 'LOADING MORE...',
    appliedLocal: 'Applied (Local)',
    readyForReview: 'Ready for Review',
    noActiveSprintTitle: 'No Active Sprint',
    noActiveSprintDesc: 'There is no active sprint in progress. Regression tracking only monitors tickets within a live sprint.',
    allSprintsClosedDesc: 'All sprints in this project are either closed or completed. You need an active or planned sprint for regression tracking.',
    noProjectSelectedTitle: 'No Project Selected',
    noProjectSelectedDesc: 'Please select a project to view the regression tracking timeline.',
    goBoard: 'Go to Board',
    noResolvedTickets: 'No resolved tickets found',
    onlyYourTicketsDesc: 'Your AI agent has not resolved any tickets in the current sprint for this project yet.',
    noSprintsTitle: 'No Sprints Planned',
    noSprintsDesc: 'We couldn\'t find any sprints in this project. Create one on the Board to start global tracking.',
    createFirstSprint: 'Create First Sprint',
    goProjects: 'Go to Projects',
    noWorkspace: 'No Active Workspace',
    noWorkspaceDesc: 'This project is not linked to a workspace. Please check your project settings.',
    accessRestricted: 'Access Restricted',
    accessRestrictedDesc: "Your role for the currently selected project doesn't have permissions to view this section.",
    switchProjectToAccess: 'Switch to a different project to gain access',
    planned: 'Planned',
    cancel: 'Cancel',
    members: 'Members',
    configuration: 'Configuration',
    access_section: 'Access Control',
    tab_api: 'API Keys',
    tab_weekly: 'Weekly Access',
    tab_personal: 'Personal Key',
    activeKey: 'Active Key',
    globalUserKey: 'Global User Key',
    yours: 'Yours',

    // Login
    loginTagline: 'Bridge the Gap Between UI and Code',
    loginTitle: 'Login to your Workspace',
    continueWithGoogle: 'Continue with Google',
    safeSecure: 'Safe & Secure',
    loginSecurityDesc: 'Enterprise-grade encryption and RBAC authorization for your snapshots.',

    // Projects Page
    projectsHeaderDesc: 'Manage your ecosystems, environments, and data security rules.',
    createProject: 'Create Project',
    availableProjects: 'Workspace Projects',
    activeWorkspace: 'Active Workspace',
    workspace_desc: 'Workspaces help you group projects and manage one subscription for all of them.',
    workspace_placeholder: 'e.g. My Agency, Personal, Enterprise',
    project_placeholder: 'e.g. Acme Website, Internal ERP...',
    emailPlaceholder: 'collaborator@gmail.com',
    confirmTransferAction: 'TRANSFER',
    projectId: 'Project Identifier',
    noProjectsSelected: 'Select a project to configure the SDK and manage team access.',
    deleteProjectConfirm: 'Are you sure you want to permanently delete this project? This action will revoke all API keys and wipe the associated ticket history.',
    deleteProject: 'Delete Project',
    delete: 'Delete',
    confirmDeleteComponentTitle: 'Delete component?',
    confirmDeleteComponentMsg: 'This will permanently remove "{name}" from this project. This action cannot be undone.',
    addMember: 'Invite Member',
    addTeamMember: 'Invite Team Member',

    // API Keys
    apiKeys: 'API Keys',
    newApiKey: 'New API Key',
    noApiKeys: 'No API keys generated for this project yet.',
    noEndpoints: 'No endpoints registered for this project yet.',
    payload: 'Payload',
    noPayload: 'No contract defined',
    endpointContract: 'Endpoint Contract',
    lastUsed: 'Last used',
    never: 'Never',
    generateApiKeyTitle: 'Generate API Key',
    generateApiKeyDesc: 'This key will grant access to manage feedback and tickets for',
    keyName: 'Key Name',
    originRestriction: 'Origin Restriction (Optional)',
    originPlaceholder: 'Leaving empty allows all origins',
    generateKey: 'Generate Key',
    keyGeneratedTitle: 'Key Generated Successfully',
    keySaveWarning: 'Store this key safely. For security reasons, it will never be shown again.',
    iveSavedIt: "I've saved it",
    generateNewKey: 'New Access Key',
    generateKeyDesc: 'Generate a unique access key to authenticate the ViewGate SDK in your application environment.',
    keyWarning: 'Make sure to copy your new API key now. You won\'t be able to see it again for security reasons.',
    allowedOrigin: 'Allowed Origin (CORS)',
    originDesc: 'Restricts requests to a specific domain (e.g., https://myapp.com). Leave empty to allow all origins.',
    manageOrigins: 'Manage Access Origins',
    originsDesc: 'Control which domains can make requests using this API key (CORS).',
    noOrigins: 'No specific origins restricted. This key currently allows requests from any domain.',
    addOrigin: 'Add Authorized Origin',
    originsUpdated: 'Origins updated successfully',
    teamManagementDesc: 'Manage collaborative access and roles for this project.',
    noWeeklyKey: 'No active weekly key for this project.',
    noPersonalKey: "You haven't generated your personal global key yet.",
    weeklyKeyWarning: '* This key expires in 7 days. You will need to regenerate it to continue using the SDK.',
    weeklyKeyExpiresIn: '* This key expires in {{days}} days. You will need to regenerate it to continue using the SDK.',
    personalKeyWarning: 'This key identifies you personally across all your projects. It is required for our MCP server to perform code edits in your local IDE with full traceability.',
    regenerateWeeklyKey: 'Regenerate Key',
    deleteWorkspace: 'Delete Workspace',
    deleteWorkspaceConfirm: 'This action is {irreversible}. All projects and member access within {name} will be lost permanently.',
    irreversible: 'irreversible',
    deleting: 'Deleting...',

    // Create Project Modal
    createNewProject: 'Create New Project',
    createProjectDesc: 'A project is a workspace for your applications and their dedicated API keys.',
    projectName: 'Project Name',

    // Board
    backlog: 'Backlog',
    todo: 'Pending',
    inProgress: 'In Progress',
    in_progress: 'In Progress',
    review: 'Review',
    done: 'Done',
    bug_fixing: 'Bugs',
    applied: 'Applied',
    searchTickets: 'Search by key, title, or message...',
    resolvedBugs: 'Resolved Bugs',
    defaultProjectKey: 'Default Project Key',
    deleteKey: 'Delete API Key',
    deleteKeyConfirm: 'Are you sure you want to delete this API key? This action cannot be undone and any application using this key will lose access.',
    findInBoard: 'Find in board...',
    allSprints: 'All Sprints',
    allPriorities: 'All Priorities',
    tags: 'Tags',
    allTags: 'All Tags',
    allCreators: 'All Creators',
    allStatuses: 'All Statuses',
    allPlanning: 'All Planning',
    unplanned: 'Unplanned',
    filters: 'Filters',
    customizeBoardView: 'Customize your board view',
    noFilterApplied: 'No filter applied',
    applyFilters: 'Apply Filters',
    apply: 'Apply',
    resetFilters: 'Reset',
    noProjectsMessage: 'You need to create or select a project to see the data.',
    selectProjectTitle: 'Select a Project',
    dateRange: 'Date Range',
    allTime: 'All Time',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    customRange: 'Custom Range',
    selectProjectDesc: 'Please select a project from the sidebar to view its details and management options.',
    noTicketsTitle: 'Your Board is Empty',
    backToDashboard: 'Back to Dashboard',
    createFirstTicket: 'Create First Ticket',
    boardEmptyDesc: 'Please create a project first to access the Scrum Board.',
    searchingServer: 'Searching through archives...',
    search: 'Search',
    createdBy: 'Created By',
    resolvedBy: 'Resolved By',
    takenBy: 'Taken by',
    appliedBy: 'Applied by',
    deployedBy: 'Deployed by',
    completedBy: 'Completed by',
    allResolvers: 'All Resolvers',
    filterMode: 'Filter Mode',
    creator: 'Creator',
    member: 'Member',
    tryDifferentSearch: 'Try a different search term',
    noResultsFound: 'No matches found',
    noResultsDesc: 'We could not find any ticket or sprint matching "{query}". We have searched your active history and all archived records.',
    ticketsFound: 'Tickets Found',
    remote: '(Remote)',
    belongsToSprint: 'Belongs to Sprint',
    noSprint: 'Backlog (No sprint)',
    sprintsFound: 'Sprints Found',
    noHistory: 'Your sprint history is empty. Once you close an active sprint, you will be able to review its results, metrics, and snapshots here.',
    moved: 'Moved',
    searchTicket: 'Search ticket',
    ticketsNotShowingInBoard: 'tickets not shown on board (check statuses or sprints)',
    ticketsHiddenByFilters: 'tickets hidden by active filters',
    projectOverview: 'Project Overview',
    priorityDistribution: 'Priority Distribution',
    completedTickets: 'Completed Tickets',
    pendingTickets: 'Pending Tickets',
    seeDetails: 'See Details',
    hideDetails: 'Hide Details',
    tickets: 'Tickets',

    // Overview
    overviewSubheader: 'Real-time health and activity for your UI snapshots.',
    startProjectTitle: 'Start your Project',
    noActiveBacklogTitle: 'No Active Backlog',
    noTicketsDesc: 'This project is set up correctly, but doesn\'t have any tickets yet. Start tracking them by installing the wrapper in your app.',
    startProjectDesc: 'Welcome to ViewGate. To start tracking tickets via the wrapper, you must first create a project and generate an API key.',
    activeProjects: 'Active Projects',
    activeSprints: 'Active Sprints',
    totalTickets: 'Total Tickets',
    avgCycleTime: 'Avg. Cycle Time',
    liveActivityFeed: 'Live Activity Feed',
    noActivityDetected: 'No activity detected yet',
    incomingTicketsDesc: 'Incoming tickets from the SDK will appear here.',
    analytics: 'Analytics',
    metric: 'Metric',
    value: 'Value',
    automationSavings: 'Automation Savings',
    inDesign: 'In Design',
    workloadCapacity: 'Workload Capacity',
    completionRate: 'Completion Rate',
    meanResolution: 'Mean Resolution',
    burnUpVelocity: 'Burn-up Velocity',
    teamVelocity: 'Team Velocity',
    weeklyGrowth: 'Weekly Growth',
    resolved: 'Resolved',
    scrum_expert: 'Scrum Expert Suggestion',
    apply_recommendation: 'Apply Suggestion',
    subscription: 'Subscription',
    trial: 'Free Trial',
    expired: 'Expired',
    active: 'Active',
    filtered: 'Filtered',
    unpaid: 'Unpaid',
    usage: 'Usage',
    daily_screenshots: 'Daily Screenshots',
    cloud_storage: 'Cloud Storage',
    unlimitedAccess: 'Unlimited Access',
    foundersUsage: 'Founders Usage',
    foundersUnlimited: 'Founders Unlimited',
    manage_subscription: 'Manage Subscription',
    pricing_per_member: '10,000 ARS per producing member (Devs, QAs, UI/UX)',
    trial_info: 'Your 30-day free trial ends on {{date}}.',
    subscribe_now: 'Subscribe Now',
    payment_required_title: 'Subscription Required',
    payment_required_desc: 'Your trial has ended. To continue using all features, please activate your monthly subscription.',
    limited_view_title: 'Filtered View (Pro Feature)',
    limited_view_desc: 'As an unpaid project, you are only seeing the latest 10 tickets. Subscribe to unlock the full backlog.',
    daily_limit_reached: 'Daily Screenshot Limit Reached',
    createdVsResolved: 'Created vs Resolved tickets over the last 14 days',
    priorityMix: 'Priority Mix',
    statusDistribution: 'Status Distribution',
    distribution: 'Distribution',
    liveActivity: 'Live Activity',
    insight: 'Analysis',
    poInsight: 'PO Vision',
    backlogWarning: 'Warning: Your backlog is growing faster than your resolution rate. Consider prioritizing critical tickets.',
    backlogGrowingWarning: 'CRITICAL: Your backlog is growing significantly faster than your team can resolve it ({{created}} new vs {{resolved}} resolved daily). High risk of delivery delays.',
    backlogGrowing: 'Attention: Your backlog is growing ({{created}} new vs {{resolved}} resolved daily). Keep an eye on resolution velocity.',
    backlogStable: 'Your backlog is stable. You are resolving tickets as they arrive ({{rate}} daily).',
    backlogImproving: 'Excellent! Your backlog is shrinking ({{resolved}} resolved vs {{created}} new daily).',
    capacityHealthy: 'Capacity is healthy. Your team is maintaining a steady resolution pace.',
    generateFullReport: 'Generate Full Report',
    tasksInQueue: 'tasks currently in queue',
    milestonesReached: 'milestones reached',
    avgTimeTriage: 'Average time from triage to done',
    activeResolutionDays: 'Active resolution days (last 14d)',
    seeAll: 'See Details',
    workloadDistribution: 'Workload Distribution',
    efficiencyFactor: 'Efficiency Factor',
    predictedFinish: 'Predicted Finish',
    noDataTitle: 'Analysis Pending',
    noDataDesc: 'Start tracking tickets to see real-time performance metrics.',
    noActiveBacklogDesc: "This project is set up correctly, but doesn't have any tickets yet. Start tracking them by installing the wrapper in your app.",
    backlogHealthy: 'All caught up! Your team is on top of the backlog.',
    goToBacklog: 'Go to Backlog',
    changeProject: 'Change Project',
    teamHealth: 'Team Health',
    owner: 'Creator',
    creatorLabel: 'Creator',
    resolver: 'Resolved by',
    plannedBy: 'Planned by',
    priority: 'Priority',
    created: 'Created',
    descriptionPlaceholder: 'Enter a description or comment...',
    noDescription: 'No additional information provided.',
    originalComment: 'Original Comment',
    noOriginalMessage: 'Original message not available.',
    newTicket: 'New Ticket',
    edit: 'Edit',
    description: 'Description/comment',
    invitationTitle: 'Project Invitation',
    invitationDesc: 'You have been invited to collaborate on',
    acceptInvitation: 'Accept Invitation',
    joinProject: 'Join Project',
    invitationAccepted: 'You have joined the project successfully!',
    invalidInvitation: 'This invitation is invalid or has expired.',
    loginToAccept: 'Please log in to accept the invitation',
    invitationEmailMismatch: 'This invitation is for {email}, but you are logged in as {currentUser}.',
    switchAccount: 'Switch Account',
    alreadyMember: 'User is already a member of this project.',
    redirectingToProjects: 'Redirecting to your projects...',
    joiningAs: 'Joining as',
    loginToViewGate: 'Login to ViewGate',
    invitationSent: 'Invitation Sent',
    invitationSentDesc: 'A secure invitation link has been sent to {{email}}.',
    cancelInvitation: 'Cancel Invitation',
    confirmCancelInvitation: 'Are you sure you want to cancel this invitation?',
    removeMember: 'Remove Member',
    confirmRemoveMember: 'Are you sure you want to remove this member from the project?',
    pending: 'Pending',
    role: 'Role',
    role_client: 'Client',
    role_pm_desc: 'Full project and team management',
    role_ux_ui_desc: 'Visual feedback and UI/UX reviews',
    role_dev_desc: 'Implementation and technical details',
    role_qa_desc: 'Testing and bug reporting',
    role_client_desc: 'Read-only access and backlog view',
    inviteToProject: 'Invite a collaborator to {{name}}',
    selectMemberRole: 'Select member role',
    sendInvitation: 'Send Invitation',
    permissionDenied: 'Permission Denied: You do not have access to this section.',
    close: 'Close',
    urgent: 'Urgent',
    noProjectsTitle: 'Welcome to ViewGate!',
    noProjectsDesc: 'Get started by creating your first project or waiting for an invitation.',
    createFirstProject: 'Create First Project',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    ready_for_review: 'Review',
    completed: 'Done',
    liveDashboard: 'Live Dashboard',
    project: 'Project',
    status: 'Status',
    createdLabel: 'Created',
    resolvedLabel: 'Resolved',
    daysLetter: 'd',
    admin: 'Admin',
    untitled: 'Untitled',
    selectOption: 'Select option...',
    noOptions: 'No options available',
    components: 'Components',
    componentsDesc: 'Create and manage UI components from Figma links. Generation and previews update progressively.',
    createComponent: 'Create Component',
    componentsList: 'Components List',
    figmaUrl: 'Figma URL',
    figmaUrlPlaceholder: 'Paste a Figma component link...',
    zipUpload: 'ZIP Upload',
    figmaLink: 'Figma Link',
    zipFile: 'ZIP File',
    zipError: 'The ZIP file must contain at least one .html and one .css file.',
    componentType: 'Component Type',
    requiredProps: 'Required props',
    noComponentsYet: 'No components created yet.',
    noPreview: 'No preview yet',
    generated: 'Generated',
    
    // E2E Testing
    e2eTestFlows: 'E2E Test Flows',
    e2eTestFlowsDesc: 'Auto-generated end-to-end tests from user interactions',
    e2eTotalFlows: 'Total Flows',
    e2eActive: 'Active',
    e2eReadyForCICD: 'Ready for CI/CD',
    e2eNeedsReview: 'Needs Review',
    e2eRequiresAttention: 'Requires attention',
    e2eAvgConfidence: 'Avg Confidence',
    e2eSelectorReliability: 'Selector reliability',
    e2eTestFlowList: 'Test Flows',
    e2eFlows: 'flows',
    e2eRefresh: 'Refresh',
    e2eNoProjectSelected: 'No project selected',
    e2eSelectProjectDesc: 'Select a project from the sidebar to view and manage your E2E test flows.',
    e2eNoFlowsYet: 'No test flows yet',
    e2eNoFlowsDesc: 'Use the ViewGate wrapper to record user interactions. Click the beaker icon in your app to start recording.',
    e2eClickBeakerHint: 'Click the beaker icon in your app',
    e2eExport: 'Export',
    e2eDelete: 'Delete',
    e2eSteps: 'Steps',
    e2eLastUpdated: 'Last updated',
    e2eCreated: 'Created',
    e2eStepsCount: 'steps',
    e2eConfidence: 'confidence',
    e2eTestSteps: 'Test Steps',
    e2eStepsTotal: 'steps total',
    e2eLowConfidence: 'Low confidence',
    e2eDeleteTitle: 'Delete Test Flow',
    e2eDeleteDescription: 'This action cannot be undone. The test flow will be permanently removed from your project.',
    e2eExportTitle: 'Test Export',
    e2eExportReady: 'Ready to use in your CI/CD pipeline',
    e2eCopyClipboard: 'Copy to Clipboard',
    e2eCopied: 'Copied!',
    e2eClose: 'Close',
    e2eNoCode: 'No code available',
    
    create: 'Create',
    addFile: 'Add File',
    dropHere: 'Drop it here',
    dragDropInfo: 'Drag and drop your ZIP file here',
    orClickToBrowse: 'or click to browse files',
    fileReady: 'File ready for upload',
    downloadPDF: 'Download PDF Report',
    executiveReport: 'Executive Project Report',
    printReport: 'Print / Export PDF',
    ticketsResolvedTotal: 'tickets resolved in total',
    transferSuccess: 'Ownership Transferred',
    transferSuccessDesc: 'Ownership has been successfully transferred. You no longer have access to this project.',
    transferError: 'Transfer Error',
    errorTransferringOwnership: 'Failed to transfer project ownership.',
    transferPM: 'Transfer Project Manager',
    designateAsPM: 'Designate as PM',
    transferWarningTitle: 'Transfer Project Ownership',
    transferWarningDesc: 'You are about to designate {{email}} as the new Project Manager. Once confirmed, you will be removed from the project and lose all access. This action cannot be undone.',
    confirmTransfer: 'Confirm Transfer',
    evidenceComparison: 'Evidence Comparison',
    evidenceBefore: 'Initial Evidence',
    evidenceAfter: 'Resolved Evidence',
    comparisonView: 'Changes Comparison',
    beforeLabel: 'Before',
    afterLabel: 'After',
    noAfterEvidence: 'No resolution evidence captured yet.',
    resolveWithCapture: 'Resolve & Capture',
    viewFull: 'View Full Screen',


    efficiencyRate: 'Efficiency Rate',
    activeBacklog: 'Tickets Pending Resolution',
    pendingActionItems: 'pending action items',
    velocityTrends: 'Velocity & Capacity Trends',
    highPriorityAttention: 'Critical Issues Needing Attention',
    noHighPriorityInsights: 'No high-priority issues detected at this time.',
    projectHealthInsights: 'Executive Project Insights',
    reportBacklogWarning: 'Your backlog is currently significantly above optimal levels.',
    reportCapacityHealthy: 'Operational capacity is stable and resolutions are consistent.',
    reportStabilityInsight: 'Project stability remains within management parameters.',
    toConfirmWrite: 'To confirm, write',
    confirmType: 'Write "{name}" to confirm',
    noWorkspaceBacklog: 'Create a project to start managing your backlog.',
    noProjectsInWorkspace: 'No projects created yet',
    noProjectsInWorkspaceDesc: 'Create a project to start managing your sprints and backlog.',
    newSprintDescription: 'Plan your next cycle of work with a new sprint.',
    sprintPlaceholder: 'Sprint #1',
    sprints: 'Sprints',
    confirmed: 'Confirmed',
    slide: 'Slide',
    noSearchResults: 'No results found',
    noSearchResultsDesc: 'We couldn\'t find any tickets matching "{{query}}". Try a different term or clear the search.',
    visualReference: 'Visual Reference',
    fetchingEvidence: 'Fetching evidence...',
    highResCapture: 'High-Res capture available',
    createSprint: 'Create Sprint',
    sprintCreated: 'Sprint created successfully',
    projectCreated: 'Project created successfully',
    workspaceCreated: 'Workspace created successfully',
    sprintName: 'Sprint Name',
    sprintDuration: 'Duration (Days)',
    noSprints: 'No sprints created yet',
    noSprintsAvailable: 'No sprints available',
    addToSprint: 'Add to Sprint',
    activeSprint: 'Active Sprint',
    plannedSprints: 'Planned Sprints',
    sprintDays: 'Days',
    startDate: 'Start Date',
    endDate: 'End Date',
    day: 'day',
    days: 'days',
    backlogSprints: 'Backlog & Sprints',
    commitmentCapacity: 'Commitment Capacity',
    sprintItems: 'Sprint Items',
    availableInBacklog: 'Available in Backlog',
    noItemsInSprint: 'No items in this sprint',
    confirmDeleteSprint: 'Delete Sprint',
    confirmDeleteSprintMsg: 'This action cannot be undone. All assigned tickets will return to the backlog.',
    confirmDeleteHistorySprintMsg: 'This action cannot be undone. All associated tickets will be permanently deleted from the system.',
    closeSprint: 'Close Sprint',
    confirmCloseSprint: 'Close Active Sprint',
    confirmCloseSprintMsg: 'Are you sure you want to close this sprint? Resolved tickets will stay in history, and pending ones will move to the next planned sprint.',
    confirmRemoveFromSprint: 'Remove from Sprint',
    confirmRemoveTicketMsg: 'This ticket will be moved back to the backlog. Are you sure?',
    deselectSprint: 'Deselect Sprint',
    selectSprintToConfirm: 'Select a Sprint',
    confirm: "Confirm",
    confirmDeleteTicket: 'Delete Ticket',
    confirmDeleteTicketMsg: 'Are you sure you want to permanently delete ticket {id}? This action cannot be undone.',
    movedToBacklog: 'Moved to Backlog',
    movedToNextSprint: 'Moved to Next Sprint',
    waitingForNextSprint: 'Waiting for next Sprint',
    movedTo: 'Moved to',
    ticketResolved: 'Ticket Resolved',
    deleted: 'Deleted',
    deletedAt: 'Deleted on',
    toastTicketDeleted: 'Ticket permanently deleted',

    // New strings from scan
    developerSettings: 'Configuration',
    settingsDesc: 'Configure your project endpoints, capacities and external integrations.',
    integrationDesc: 'Connect your workspace with third-party services to automate your workflow.',
    figmaConnected: 'Figma Connection',
    figmaDesc: 'Sync your design tokens and components directly from Figma files.',
    correctionHistory: 'Correction History',
    deleteCorrectionTitle: 'Delete Correction',
    confirmDeleteCorrection: 'Are you sure you want to permanently delete this correction? This action cannot be undone.',
    fixDescriptionLabel: 'How did you fix this?',
    fixDescriptionPlaceholder: 'Describe your changes and fixes...',
    pendingFix: 'Pending Fix',
    resolvedFix: 'Resolved',
    appliedFix: 'Applied by AI',
    appliedByAI: 'Applied by AI Agent',
    jiraConfig: 'Jira Cloud Configuration',
    azureConfig: 'Azure DevOps Configuration',
    serverUrl: 'Server URL',
    apiToken: 'API Token',
    projectKey: 'Project Key',
    orgUrl: 'Organization URL',
    enabled: 'Enabled',
    requiredFieldsMissing: 'Required fields missing',
    sprintSettings: 'Capacity & Planning',
    capacityDesc: 'Configure team velocity and the allowed workload for the AI Agent.',
    aiAgentSettings: 'AI Agent Configuration',
    aiAgentSettingsDesc: 'Manage the operational capacity and behavior of the MCP Agent.',
    defaultCapacity: 'Default Day Capacity',
    sprintLimit: 'AI Agent Capacity',
    sprintLimitDesc: 'Maximum number of concurrent tickets the AI agent can process.',
    backendEndpoints: 'Backend Endpoints',
    endpointsDesc: 'Manage and document your API routes for the ViewGate wrapper.',
    routes: 'routes',
    lastSynced: 'Last Synced',
    apiAccessKeys: 'API Access Keys',
    apiAccessKeysDesc: 'Use these keys to authenticate the ViewGate SDK in your applications. Never share your full keys in public client-side code unless using domain restrictions.',
    keyPrefix: 'Key Prefix',
    action: 'Action',
    registerWebhook: 'Register New Webhook endpoint',
    sharedVsPersonalKeys: 'Shared vs Personal Keys',
    sharedVsPersonalKeysDesc: 'Team keys require an actorId for traceability. Personal keys map directly to your user account.',
    noMembers: 'No members yet.',
    emailAddress: 'Email Address',
    inviteDesc: 'Enter the Gmail address of the person you want to invite to **{name}**.',
    planned_status: 'Planned',
    moved_status: 'Moved',
    loading: 'Loading',
    authenticating: 'Authenticating...',
    ok: 'OK',
    error: 'Error',
    errorCreatingProject: 'Could not create project. Please try again.',
    keyError: 'Key Error',
    errorGeneratingKey: 'Could not generate API key. Verify permissions.',
    invitationError: 'Invitation Error',
    errorInvitingMember: 'Could not invite member.',
    tier_limit_reached: 'Tier Limit Reached',
    pm_limit_reached: 'Project Manager Limit Reached',
    pmLimitNote: 'Only 2 Project Managers are allowed per project.',
    pmLimitReachedShort: 'Limit: 2',
    deletionError: 'Deletion Error',
    errorDeletingProject: 'There was an error trying to delete the project.',
    allOrigins: 'All Origins',
    projectNamePlaceholder: 'e.g. Acme Website, Internal ERP...',
    keyNamePlaceholder: 'e.g. Staging Server, Production CRM...',
    originExample: 'e.g. localhost:5173',
    understood: 'Understood',
    revokeKeyConfirm: 'Are you sure you want to revoke this API key? This action cannot be undone.',
    failedRevokeKey: 'Failed to revoke key',
    more: 'more',
    loadMore: 'Load More',
    noResults: 'No results found',

    // Toast Feedback
    toastError: 'An unexpected error occurred while processing your request.',
    toastSprintDeleted: 'Sprint successfully deleted. Tickets have been returned to the backlog.',
    toastSprintOverlapError: 'Could not create sprint: Selected dates overlap with an existing active or planned sprint.',
    toastTicketAdded: 'Ticket successfully assigned to the sprint.',
    toastTicketRemoved: 'Ticket removed from the sprint and returned to the backlog.',
    toastSprintClosed: 'Sprint successfully closed. Summary report generated.',
    toastHistorySprintDeleted: 'Sprint successfully removed from history.',
    toastMemberRemoved: 'Member removed successfully.',
    toastInvitationCancelled: 'Invitation cancelled successfully.',
    aiFixSummary: 'AI Fix Summary',
    aiFixSummaryDesc: 'Summary of changes applied by the AI',
    aiVerified: 'AI Verified',

    // Planning
    planningAI: 'AI Planning',
    complexity: 'Complexity',
    uncertainty: 'Uncertainty',
    impact: 'Impact',
    score: 'Score',
    aiAnalysis: 'AI Analysis',
    type: 'Type',
    totalPoints: 'Total Points',
    predictedCapacity: 'Estimated Capacity',
    capacity: 'Capacity',
    planningRequiredToSprint: 'Planning Required: This ticket must be analyzed by AI before it can be added to a sprint.',
    planningRequiredTitle: 'Planning Required',
    aiFriendly: 'AI-friendly',
    aiAssisted: 'AI-assisted',
    humanCritical: 'Human-critical',

    // Tooltips
    complexityTooltip: '1: Simple (UI/text)\n2: Moderate logic\n3: Complex (architecture/integrations)',
    uncertaintyTooltip: '1: Well-defined\n2: Some ambiguity\n3: Unclear/Very open',
    impactTooltip: '1: Minor improvement\n2: Useful feature\n3: Business critical',
    riskTooltip: '1: Low (no deps)\n2: Moderate\n3: High (multi-deps/core)',
    scoreTooltip: 'Estimated effort\n(Complexity² + Uncertainty + Risk)',
    priorityTooltip: 'Relative urgency\n(Impact / Score)',
    typeTooltip: 'AI suitability for automated resolution',
    sprintOccupied: 'Date occupied',
    sprintOverlapWarning: 'Overlap with',
    emptyBacklogTitle: 'Backlog Cleared!',
    emptyBacklogMessage: 'All tickets have been planned or resolved. Your project is up to date.',
    noResultsTitle: 'No matches found',
    emptySprintTitle: 'Empty Sprint: No items assigned',
    noResultsMessage: 'Refine your search or check your spelling.',
    noSprintSelected: 'No Sprint Selected',
    selectSprintToView: 'Select a sprint from the sidebar to view its items and progress.',
    loadingHistory: 'Loading history...',
    toastSettingsSaved: "Settings saved successfully",
    saveSettings: 'Save Settings',

    // Documentation Modals
    installation: 'Installation',
    basicSetup: 'Basic Setup',
    sdkSetupDesc: 'Wrap your main App component with the ViewGate provider to enable real-time UI capture and feedback logs.',
    newFeatures: 'New Features',
    searchableEndpoints: 'Searchable Endpoints',
    searchableEndpointsDesc: 'Map your backend logic to UI elements with the new searchable selector directly in the wrapper.',
    dynamicFocusAreas: 'Dynamic Focus Areas',
    dynamicFocusAreasDesc: 'The wrapper now dynamically hides Backend and UX UI areas if they are not configured or connected.',
    autoCapture: 'Auto-Capture',
    autoCaptureDesc: 'Automatically tracks UI changes and correlates them with your codebase using source maps.',
    offlineReady: 'Offline Ready',
    offlineReadyDesc: 'Queues feedback locally and syncs automatically when connection is restored.',
    mcpIntro: 'Model Context Protocol (MCP) allows ViewGate to communicate with your AI assistant to perform native code edits with concurrency protection and ticket locking.',
    localizedIntelligence: 'Surgical intelligence with full AI traceability.',
    npmInstall: 'Step 1: Install Package',
    npmInstallDesc: 'Install the core wrapper package in your project.',
    step2UnifiedSetup: 'Step 2: Unified Setup',
    step2UnifiedSetupDesc: 'This command configures your .env and connects the MCP tools to your project.',
    step3Configuration: 'Step 3: Add API Key',
    mcpConfigDesc: "Open your IDE's MCP configuration and paste the Project API Key and your Personal Key from the dashboard into the generated config file for full AI traceability.",
    unsavedChanges: 'Unsaved Changes',
    unsavedChangesDesc: 'You have unsaved changes in your settings. Do you want to save them before leaving?',
    discard: 'Discard',
    stay: 'Stay',
    save: 'Save',
    availableTools: 'Available Tools',
    syncEndpointsDesc: 'Automatic API route scanning and high-performance batch synchronization.',
    getAnnotationsDesc: 'Get surgical fix instructions with Ticket Locking (1h) to prevent agent collisions.',
    planningDesc: 'Analyze backlog with AI-driven complexity and risk assessment.',
    markReadyDesc: 'Update tickets with AI summaries. Supports batch processing and automatic retries.',
    keyBenefits: 'Key Benefits',
    automatedBugFixing: 'Automated Bug Fixing',
    contextAwareRefactoring: 'Context-Aware Refactoring',
    directUiToCodeSync: 'Direct UI-to-Code Sync',
    aiDrivenPlanning: 'AI-Driven Sprint Planning',
    capacityPrediction: 'Automated Capacity Prediction',
    dataPrivacy: 'Data Privacy',
    dataPrivacyDesc: 'ViewGate never stores your entire codebase. We only capture scoped UI snapshots and metadata required to locate components within your files.',
    compliance: 'Compliance',
    complianceDesc: 'Our infrastructure is SOC2 Type II compliant. All data is encrypted in transit via TLS 1.3 and at rest using AES-256.',
    securityArchitecture: 'Security Architecture',
    isolatedWorkspace: 'Isolated Workspace',
    isolatedWorkspaceDesc: 'Each project runs in a cryptographically isolated container.',
    rbacAuth: 'RBAC Authorization',
    rbacAuthDesc: 'Granular access control for viewing code snapshots.',
    sourceTraceability: 'Source Traceability',
    sourceTraceabilityDesc: 'Every AI edit is signed and traceable back to a specific session.',
    versionStable: 'Latest Version • 2026 Stable',
    needHelpSecurity: 'Need help? Contact our security team at',
    fullDocs: 'full docs',
    initiatePlanning: 'Initiate Planning',
    lockCapacity: 'Lock Capacity',
    suggestNext: 'Suggest Next',
    capacityMeter: 'Capacity Meter',
    targetCapacity: 'Target Capacity',
    empiricalCapacity: 'Empirical Capacity',
    uncertaintyFactor: 'Uncertainty Factor',
    teamEfficiency: 'Team Efficiency',
    backlogQuality: 'Backlog Quality',
    planningPhase: 'Planning Phase',
    buildPhase: 'Build Phase',
    lockPhase: 'Lock Phase',
    draft: 'Draft',
    planning: 'Planning',
    safe: 'Safe',
    warning: 'Warning',
    overflow: 'Overflow',
    points: 'Points',
    remaining: 'Remaining',
    totalCapacity: 'Total Capacity',
    suggestNextDesc: 'Find the best ticket based on priority and remaining capacity.',
    lockPlanningDesc: 'Finalize capacity and move to construction phase.',
    lockPlanningDescWithPoints: 'Your team will start this sprint with an estimated capacity of {points} points for {days} days. Finalize planning to move to the build phase.',
    capacityCalculated: 'Capacity Calculated',
    uncertaintyPenalty: 'Uncertainty Penalty',
    suggestionFound: 'Best suggestion found!',
    noCompatibleTickets: 'No compatible tickets found for remaining capacity.',
    aiLanguage: 'AI Analysis Language',
    aiLanguageDesc: 'Set the language for AI-generated planning comments.',
    testingFramework: 'Testing Framework',
    testingFrameworkDesc: 'Default testing framework for generating E2E test flows from captured user interactions.',
    e2eMCPProcessed: 'AI Processed',
    e2eCaptured: 'Raw Captured',
    spanish: 'Spanish',
    sprintItemDeleted: 'Sprint item deleted',
    confirmSignOut: 'Sign Out Confirmation',
    confirmSignOutMsg: 'Are you sure you want to sign out?',
    copyKey: 'Copy Key',
    focusDetails: 'Focus Details',
    personalKey: 'Personal Key',
    personalKeyDesc: 'This key identifies you when resolving tickets via the MCP. Keep it private.',
    weeklyAccessKey: 'Weekly Access Key',
    weeklyKeyDesc: 'This key is valid for 7 days and identifies your user within this specific project. Use it to authenticate the ViewGate Wrapper in your environment.',
    generateWeeklyKey: 'Generate Weekly Key',
    weeklyKeyGenerated: 'Weekly Key generated successfully',
    appliedLocally: 'Applied Locally',
    pendingDeploy: 'Pending Deploy',
    aiApplied: 'AI Applied',
    generatePersonalKey: 'Generate Personal Key',
    regeneratePersonalKey: 'Regenerate Personal Key',
    personalKeyGenerated: 'Personal Key generated successfully',
    role_pm: 'Project Manager',
    role_owner: 'Project Manager',
    role_role_owner: 'Project Manager',
    role_dev: 'Developer',
    role_ux_ui: 'UX UI Designer',
    role_qa: 'QA Engineer',
    moveToReview: 'Move to Review',
    returnToPending: 'Return to Pending',
    returnToBugs: 'Return to Bugs',
    completeTicket: 'Complete Ticket',
    confirmStatusTitle: 'Confirm Status Change',
    confirmStatusMessage: 'Are you sure you want to change the status of this ticket to {status}?',
    affectedEndpoints: 'Affected Endpoints',
    uxReferences: 'UX References',
    copied: 'Copied!',
    sprintHealth: 'Sprint Health',
    sprintStatus: 'Sprint Status',
    healthy: 'Healthy',
    risk: 'Risk',
    critical: 'Critical',
    pointLoad: 'Point Load',
    sprintStarting: 'Sprint Starting',
    progress: 'Progress',
    timeElapsed: 'Time Elapsed',
    healthBehindSchedule: 'Severely behind schedule',
    healthTooManyUrgent: 'Multiple urgent tickets pending',
    healthBugOverflow: 'Bug fixing overload',
    healthSlightlyBehind: 'Lagging behind progress',
    healthUrgentPending: 'Urgent tickets awaiting attention',
    healthHighWipBugs: 'High WIP on bug fixing',
    healthHealthy: 'Sprint healthy and on track',
    externalIntegrations: 'External Integrations',
    importantNote: 'IMPORTANT',
    evidenceBypassWarning: 'Warning: Completing the ticket from here will not generate an "After" evidence capture. To include it, use the "Resolve with Evidence" button in the wrapper.',
    requiresCorrections: 'Requires Corrections',
    correctionsRequired: 'Corrections Required',
    correctionsDesc: 'Please, detail the necessary corrections for the developer.',
    correctionsPlaceholder: 'Write your feedback here...',
    correctionsMandatory: 'Feedback is mandatory to move back',
    sendCorrections: 'Send & Revert Ticket',
    iaCommentWarning: "When moving to review from the dashboard, the automatic AI comment on applied changes will not be generated. To include it, resolve the ticket directly from your IDE using the MCP.",
    uploadManualEvidence: "Upload Manual Evidence",
    uploadManualEvidenceDesc: "Upload a screenshot to validate the changes made.",
    uploadingEvidence: "Uploading",
    uploadSuccess: "Evidence uploaded successfully",
    uploadError: "Error uploading evidence",
    selectImage: "Select Image",
    validatedEvidence: "Validated Evidence",
    platinum_level: 'Platinum Level',
    elevate_team: 'Elevate your team to',
    platinum_desc: 'Unlock higher limits, full backlog history and advanced Scrum Expert intelligence starting at $10,000 ARS per member.',
    active_billing_members: 'active members for billing',
    monthly_cost_label: 'Monthly Cost',
    subscription_disclaimer: 'Monthly recurring subscription. You can cancel at any time from your Mercado Pago panel.',
    privacy_guaranteed: 'Privacy Guaranteed',
    privacy_desc: 'Your captures and data are encrypted. We do not sell your company information. Military-grade privacy.',
    free_observers: 'Free Observers',
    free_observers_desc: 'You only pay for those who "do". Project Managers and Clients have full read access at no additional cost.',
    faq_title: 'Frequently Asked Questions',
    faq_q1: 'How is the cost calculated?',
    faq_a1: 'We count every active member of your workspace (PMs, Developers, Designers, QA). Pricing depends on your tier ($10k ARS for Pro, $25k for Enterprise) multiplied by the total unique head count.',
    faq_q2: 'What happens if I don\'t pay?',
    faq_a2: 'Free tier workspaces are limited to 5 unique members, 3 projects, 15 captures per day, and 100MB of storage.',
    faq_q3: 'Is Mercado Pago safe?',
    faq_a3: 'Yes, it is the leading processor in Latin America. We never see your card data.',
    partnership_label: 'In partnership with',
    enterprise_plan_q: 'Do you need an Enterprise plan?',
    enterprise_plan_a: 'More than 50 users, invoice A and 24/7 support.',
    dailyCaptures: 'Daily Captures',
    backlogAccess: 'Backlog Access',
    scrumExpert: 'Scrum Expert',
    integrations: 'Integrations',
    branding: 'Custom Branding',
    standard_vs_platinum: 'Standard vs Platinum',
    level_comparison: 'Level Comparison',
    platinum_upgrade_cta: 'Get Platinum Pro',
    active_subscription: 'Active Subscription',
    loading_plans: 'Loading Plans...',
    recommended: 'Recommended',
    contact_sales: 'Contact Sales',
    contact_support: 'Contact Support',
    team_access: 'All-Access Team (Except Clients)',
    team_access_desc: 'Every member contributes to the value flow. We charge per person (excluding Clients) to ensure everyone has full access to the AI and history.',
    plan_standard_label: 'Standard',
    plan_platinum_label: 'Platinum Pro',
    plan_founders: 'Plan Founders',
    plan_pro: 'Plan Pro',
    plan_enterprise: 'Plan Enterprise',
    plan_free: 'Plan Free',
    security_guaranteed: 'Security Guaranteed',
    val_unlimited: 'Unlimited',
    val_full_history: 'Full History',
    val_advanced_ai: 'Advanced AI',
    val_sync_pro: 'Sync Pro (Jira/Figma)',
    val_custom_branding: 'Custom Branding',
    val_last_10: 'Last 10 Tickets',
    val_basic_analysis: 'Basic Analysis',
    val_manual_only: 'Manual only',
    val_viewgate_logo: 'ViewGate Logo',
    val_free_forever: 'Free Forever',
    val_per_user: 'Per User',
    val_3_per_day: '3 per day',
    storage_limit: 'Storage Quota',
    unique_users: 'Team Members',
    unlimited: 'Unlimited',
    loading_pricing_options: 'Loading your options...',
    elevate_workspace: 'Elevate your Workspace',
    workspace_billing_desc: 'Workspaces allow you to group multiple projects under a single subscription. Your plan covers all projects within the active workspace.',
    unique_billable_users: 'Unique Billable Users',
    upgrade_workspace_cta: 'Upgrade Workspace',
    faq_q4: 'How does the Enterprise Plan work?',
    faq_a4: 'It is designed for large teams needing custom limits, dedicated SLAs, and priority 24/7 support. Contact us for a tailored plan.',
    support_billing: 'Support & Billing',
    still_have_questions: 'Still have questions?',
    response_time_2h: 'Response in <span class="text-emerald-500">less than 2h</span>',
    no_workspaces_found: 'No Workspaces Found',
    only_pm_manage_sub: 'Subscriptions can only be managed by the Project Manager who created the workspace. You are currently not the owner of any workspaces.',
    payment_confirmed: 'Payment Confirmed',
    payment_pending: 'Payment Processing',
    workspace_updated_success: 'Your workspace has been updated. Enjoy your new benefits!',
    processing_subscription_wait: 'We are processing your subscription. It will be active in a few seconds.',
    contactFormTitle: 'Contact Support',
    contactFormDesc: 'Send us a message and we will get back to you shortly.',
    submitConsultation: 'Send Inquiry',
    consultationSuccess: 'Inquiry Sent Successfully',
    redirectingSoon: 'Closing window...',
    subject: 'Subject',
    message: 'Message',
    email_placeholder: 'your@email.com',
    subject_placeholder: 'Brief summary of your inquiry...',
    subject_support: 'Support Inquiry',
    subject_sales: 'Enterprise Plan Inquiry',
    message_placeholder: 'Type your message details here...',
    secure_intake_protocol: 'ViewGate Secure Intake Protocol v2.5',
    redirecting_to_mercadopago: 'Redirecting to Mercado Pago...',
    verifying_payment: 'Verifying with payment gateway...',
    pendingPlanning: 'Awaiting Planning',
    themePreview: 'Theme Preview',
    live: 'Live'
  },
  es: {
    // Nav & General
    overview: 'Resumen',
    projects: 'Proyectos',
    manageProjects: 'Administrar proyectos',
    board: 'Tablero',
    uiImprovements: 'Mejoras UI',
    signOut: 'Cerrar Sesión',
    searchPlaceholder: 'Buscar tickets, clientes, ajustes...',
    noNotifications: 'No hay notificaciones',
    profile: 'Perfil',
    language: 'Idioma',
    inDevelopment: 'En Desarrollo',
    history: 'Historial',
    activeProject: 'Proyecto Activo',
    regressionTracking: 'Seguimiento de MCP',
    regressionTrackingDesc: 'Seguimiento de los ajustes de código resueltos por tus agentes IA.',
    onlyYourTickets: 'Resoluciones Personales',
    agentId: 'ID del Agente',
    resolvedByYou: 'Resueltos por Vos',
    totalTicketsHandled: 'Total Resueltos vía MCP',
    pendingDeployment: 'Pendientes de Deploy',
    liveToday: 'DESPLEGADOS',
    deployed: 'DESPLEGADO',
    appliedStatus: 'Aplicado (Local)',
    deploymentAction: 'Desplegar a Review',
    ticketTimeline: 'Línea de Tiempo de Estado',
    regressionEventLog: 'HISTORIAL DE ACTIVIDAD',
    mcpEventLog: 'Historial de Actividad MCP',
    timelinePending: 'Ticket registrado en el sistema',
    timelineLocked: 'IA PROCESANDO',
    timelineApplied: 'APLICADO LOCAL',
    timelineReady: 'DESPLEGADO',
    waitForSync: 'LISTO PARA SINCRONIZAR',
    deploymentSuccess: 'DESPLIEGUES VERIFICADOS',
    initializingTracking: 'INICIANDO SEGUIMIENTO...',
    aiProcessing: 'IA PROCESANDO',
    loadingMore: 'CARGANDO MÁS...',
    appliedLocal: 'Aplicado (Local)',
    readyForReview: 'Listo para Review',
    noActiveSprintTitle: 'Sin Sprint Activo',
    noActiveSprintDesc: 'No hay un sprint activo en ejecución. El seguimiento de regresión solo monitorea tickets dentro de un sprint vivo.',
    allSprintsClosedDesc: 'Todos los sprints de este proyecto están cerrados o completados. Necesitas un sprint activo o planificado para el seguimiento de regresión.',
    noProjectSelectedTitle: 'Proyecto no Seleccionado',
    noProjectSelectedDesc: 'Por favor, selecciona un proyecto para ver la línea de tiempo de seguimiento de regresión.',
    goBoard: 'Ir al Board',
    noResolvedTickets: 'No se han encontrado tickets resueltos',
    onlyYourTicketsDesc: 'Tu agente de IA no ha resuelto ningún ticket aún en el sprint actual de este proyecto.',
    noSprintsTitle: 'No hay Sprints Planificados',
    noSprintsDesc: 'No hemos encontrado ningún sprint en este proyecto. Crea uno en el Tablero para iniciar el seguimiento global.',
    createFirstSprint: 'Crear Primer Sprint',
    goProjects: 'Ir a Proyectos',
    noWorkspace: 'Sin Espacio Activo',
    noWorkspaceDesc: 'Este proyecto no está vinculado a un espacio de trabajo. Por favor, revisa la configuración del proyecto.',
    workspace_desc: 'Los espacios de trabajo te permiten agrupar proyectos y gestionar una única suscripción para todos ellos.',
    workspace_placeholder: 'ej. Mi Agencia, Personal, Empresa',
    project_placeholder: 'ej. Sitio Web Acme, ERP Interno...',
    emailPlaceholder: 'colaborador@gmail.com',
    confirmTransferAction: 'TRANSFERIR',
    accessRestricted: 'Acceso Restringido',
    accessRestrictedDesc: "Tu rol para el proyecto seleccionado no tiene permisos para ver esta sección.",
    switchProjectToAccess: 'Selecciona otro proyecto para obtener acceso',
    cancel: 'Cancelar',
    members: 'Miembros',
    configuration: 'Configuración',
    access_section: 'Control de Accesos',
    tab_api: 'Claves API',
    tab_weekly: 'Acceso Semanal',
    tab_personal: 'Clave Personal',
    activeKey: 'Clave Activa',
    globalUserKey: 'Clave Global de Usuario',
    yours: 'Tuya',

    // Login
    loginTagline: 'Cierra la brecha entre la UI y el Código',
    loginTitle: 'Inicia sesión en tu espacio',
    continueWithGoogle: 'Continuar con Google',
    safeSecure: 'Seguro & Protegido',
    loginSecurityDesc: 'Cifrado de grado empresarial y autorización RBAC para tus capturas.',

    // Projects Page
    projectsHeaderDesc: 'Gestiona tus ecosistemas, entornos y reglas de seguridad de datos.',
    createProject: 'Crear Proyecto',
    availableProjects: 'Proyectos del Workspace',
    activeWorkspace: 'Workspace Activo',
    projectId: 'Identificador del Proyecto',
    noProjectsSelected: 'Selecciona un proyecto para configurar el SDK y gestionar accesos.',
    deleteProjectConfirm: '¿Estás seguro de que deseas eliminar este proyecto de forma permanente? Esta acción revocará todas las claves API y eliminará el historial de tickets asociado.',
    deleteProject: 'Eliminar Proyecto',
    addMember: 'Invitar Miembro',
    addTeamMember: 'Invitar Miembro al Equipo',

    // API Keys
    apiKeys: 'Claves API',
    newApiKey: 'Nueva Clave API',
    noApiKeys: 'Aún no se han generado claves API para este proyecto.',
    noEndpoints: 'Sin endpoints registrados para este proyecto todavía.',
    payload: 'Payload',
    noPayload: 'Sin contrato definido',
    endpointContract: 'Contrato del Endpoint',
    lastUsed: 'Último uso',
    never: 'Nunca',
    generateApiKeyTitle: 'Generar Clave API',
    generateApiKeyDesc: 'Esta clave otorgará acceso para gestionar feedback y tickets para',
    keyName: 'Nombre de la Clave',
    originRestriction: 'Restricción de Origen (Opcional)',
    originPlaceholder: 'Dejar vacío permite todos los orígenes',
    generateKey: 'Generar Clave',
    keyGeneratedTitle: 'Clave Generada Exitosamente',
    keySaveWarning: 'Guarda esta clave de forma segura. Por razones de seguridad, no se volverá a mostrar.',
    iveSavedIt: 'La he guardado',
    generateNewKey: 'Nueva Llave de Acceso',
    generateKeyDesc: 'Genera una llave de acceso única para autenticar el SDK de ViewGate en tu entorno de aplicación.',
    keyWarning: 'Asegúrate de copiar tu nueva clave API ahora. No podrás volver a verla por razones de seguridad.',
    allowedOrigin: 'Origen Permitido (CORS)',
    originDesc: 'Restringe las solicitudes a un dominio específico (ej. https://miapp.com). Deja vacío para permitir todos los orígenes.',
    manageOrigins: 'Gestionar Orígenes de Acceso',
    originsDesc: 'Controla qué dominios pueden realizar peticiones usando esta clave API (CORS).',
    noOrigins: 'No hay orígenes restringidos. Esta clave permite peticiones desde cualquier dominio.',
    addOrigin: 'Agregar Origen Autorizado',
    originsUpdated: 'Orígenes actualizados con éxito',
    teamManagementDesc: 'Gestiona el acceso colaborativo y los roles para este proyecto.',
    noWeeklyKey: 'No hay ninguna clave semanal activa para este proyecto.',
    noPersonalKey: 'Aún no has generado tu clave personal global.',
    weeklyKeyWarning: '* Esta clave expira en 7 días. Deberás regenerarla para continuar usando el SDK.',
    weeklyKeyExpiresIn: '* Esta clave expira en {{days}} días. Deberás regenerarla para continuar usando el SDK.',
    personalKeyWarning: 'Esta clave te identifica personalmente en todos tus proyectos. Es requerida para que nuestro servidor MCP realice ediciones de código en tu IDE local con trazabilidad completa.',
    regenerateWeeklyKey: 'Regenerar Clave',
    deleteWorkspace: 'Eliminar Workspace',
    deleteWorkspaceConfirm: 'Esta acción es {irreversible}. Todos los proyectos y el acceso de los miembros dentro de {name} se perderán permanentemente.',
    irreversible: 'irreversible',
    deleting: 'Eliminando...',

    // Create Project Modal
    createNewProject: 'Crear Nuevo Proyecto',
    createProjectDesc: 'Un proyecto es un espacio de trabajo para tus aplicaciones y sus claves API dedicadas.',
    projectName: 'Nombre del Proyecto',

    // Board
    backlog: 'Backlog',
    todo: 'Pendientes',
    inProgress: 'En Progreso',
    in_progress: 'En Progreso',
    review: 'Revisión',
    done: 'Completados',
    bug_fixing: 'Errores',
    applied: 'Aplicado',
    resolvedBugs: 'Errores Resueltos',
    defaultProjectKey: 'Clave de Proyecto por Defecto',
    deleteKey: 'Eliminar Clave API',
    deleteKeyConfirm: '¿Estás seguro de que quieres eliminar esta clave API? Esta acción no se puede deshacer y cualquier aplicación que use esta clave perderá el acceso.',
    findInBoard: 'Buscar en el tablero...',
    allSprints: 'Todos los Sprints',
    allPriorities: 'Todas las Prioridades',
    tags: 'Etiquetas',
    allTags: 'Todos los Tags',
    allCreators: 'Todos los Creadores',
    allStatuses: 'Todos los Estados',
    allPlanning: 'Todo el Plan',
    unplanned: 'No Planificados',
    filters: 'Filtros',
    customizeBoardView: 'Personaliza tu vista del tablero',
    noFilterApplied: 'Sin filtro aplicado',
    applyFilters: 'Aplicar Filtros',
    apply: 'Aplicar',
    resetFilters: 'Reiniciar',
    selectProject: 'Seleccionar Proyecto',
    selectSprint: 'Seleccionar Sprint',
    noProjectsMessage: 'Necesitas crear o seleccionar un proyecto para ver los datos.',
    selectProjectTitle: 'Selecciona un Proyecto',
    dateRange: 'Rango de Fecha',
    allTime: 'Todo el tiempo',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    thisMonth: 'Este Mes',
    customRange: 'Rango Personalizado',
    selectProjectDesc: 'Por favor, selecciona un proyecto de la barra lateral para ver sus detalles y opciones de gestión.',
    noTicketsTitle: 'Tu Tablero está Vacío',
    backToDashboard: 'Volver al Dashboard',
    createFirstTicket: 'Crear Primer Ticket',
    boardEmptyDesc: 'Por favor, crea un proyecto primero para acceder al Tablero Scrum.',
    searchingServer: 'Buscando en los archivos...',
    search: 'Buscar',
    createdBy: 'Creado por',
    resolvedBy: 'Resuelto por',
    takenBy: 'Tomado por',
    appliedBy: 'Aplicado por',
    deployedBy: 'Desplegado por',
    completedBy: 'Completado por',
    allResolvers: 'Todos los Resolutores',
    filterMode: 'Modo de Filtro',
    creator: 'Creador',
    member: 'Miembro',
    tryDifferentSearch: 'Intenta con otro término de búsqueda',
    noResultsFound: 'No se encontraron coincidencias',
    noResultsDesc: 'No pudimos encontrar ningún ticket o sprint que coincida con "{query}". Hemos buscado en tu historial activo y en todos los registros archivados.',
    ticketsFound: 'Tickets Encontrados',
    sprintsFound: 'Sprints Encontrados',
    noHistory: 'Tu historial de sprints está vacío. Una vez que cierres un sprint activo, podrás revisar sus resultados, métricas y capturas aquí.',
    belongsToSprint: 'Pertenece al Sprint',
    noSprint: 'Backlog (Sin sprint)',
    planned: 'Planificado',
    searchTickets: 'Buscar por clave, título o mensaje...',
    searchTicket: 'Buscar ticket',
    ticketsNotShowingInBoard: 'tickets no mostrados en el tablero (revisa estados o sprints)',
    ticketsHiddenByFilters: 'tickets ocultos por filtros activos',
    projectOverview: 'Resumen del Proyecto',
    priorityDistribution: 'Distribución de Prioridades',
    completedTickets: 'Tickets Completados',
    pendingTickets: 'Tickets Pendientes',
    seeDetails: 'Ver Detalles',
    hideDetails: 'Ocultar Detalles',
    tickets: 'Tickets',

    // Overview
    overviewSubheader: 'Estado y actividad en tiempo real de tus capturas de UI.',
    startProjectTitle: 'Inicia tu Proyecto',
    noActiveBacklogTitle: 'Sin Backlog Activo',
    noTicketsDesc: 'Este proyecto está configurado correctamente, pero aún no tiene tickets registrados. Comienza a rastrearlos instalando el wrapper en tu aplicación.',
    startProjectDesc: 'Bienvenido a ViewGate. Para empezar a rastrear tickets mediante el wrapper, primero debes crear un proyecto y generar una clave API.',
    activeProjects: 'Proyectos Activos',
    activeSprints: 'Sprints Activos',
    totalTickets: 'Tickets Totales',
    avgCycleTime: 'Ciclo Promedio',
    liveActivityFeed: 'Actividad en Vivo',
    noActivityDetected: 'No hay actividad detectada',
    incomingTicketsDesc: 'Los tickets del SDK aparecerán aquí.',
    analytics: 'Analíticas',
    metric: 'Métrica',
    value: 'Valor',
    automationSavings: 'Ahorro por Automatización',
    inDesign: 'En Diseño',
    workloadCapacity: 'Capacidad de Trabajo',
    completionRate: 'Tasa de Finalización',
    meanResolution: 'Resolución Media',
    burnUpVelocity: 'Velocidad de Cierre',
    teamVelocity: 'Velocidad del Equipo',
    weeklyGrowth: 'Crecimiento Semanal',
    resolved: 'Resueltos',
    scrum_expert: 'Sugerencia de Scrum Expert',
    apply_recommendation: 'Aplicar Sugerencia',
    subscription: 'Suscripción',
    trial: 'Prueba Gratuita',
    expired: 'Expirado',
    unpaid: 'Impago',
    usage: 'Consumo',
    daily_screenshots: 'Capturas Diarias',
    cloud_storage: 'Almacenamiento',
    unlimitedAccess: 'Acceso Ilimitado',
    foundersUsage: 'Consumo Founders',
    foundersUnlimited: 'Founders Ilimitado',
    manage_subscription: 'Gestionar Suscripción',
    pricing_per_member: '10.000 ARS por miembro productor (Devs, QAs, UI/UX)',
    trial_info: 'Tu prueba de 30 días termina el {{date}}.',
    subscribe_now: 'Suscribirse Ahora',
    payment_required_title: 'Suscripción Requerida',
    payment_required_desc: 'Tu periodo de prueba ha finalizado. Para seguir usando todas las funciones, por favor activa tu suscripción mensual.',
    limited_view_title: 'Vista Filtrada (Función Pro)',
    limited_view_desc: 'En el modo gratuito solo puedes ver los últimos 10 tickets. Suscríbete para desbloquear todo el backlog.',
    daily_limit_reached: 'Límite Diario de Capturas Alcanzado',
    createdVsResolved: 'Tickets creados vs resueltos en los últimos 14 días',
    priorityMix: 'Mix de Prioridades',
    statusDistribution: 'Distribución por Estado',
    distribution: 'Distribución',
    liveActivity: 'Actividad en Vivo',
    insight: 'Análisis',
    poInsight: 'Visión del PO',
    backlogWarning: 'Advertencia: Tu backlog crece más rápido que tu tasa de resolución. Considera priorizar tickets críticos.',
    backlogGrowingWarning: 'CRÍTICO: Tu backlog crece significativamente más rápido de lo que tu equipo puede resolver ({{created}} nuevos vs {{resolved}} resueltos al día). Alto riesgo de retrasos.',
    backlogGrowing: 'Atención: Tu backlog está creciendo ({{created}} nuevos vs {{resolved}} resueltos al día). Monitorea la velocidad de resolución.',
    backlogStable: 'Tu backlog está estable. Resuelves los tickets al ritmo que llegan ({{rate}} al día).',
    backlogImproving: '¡Excelente! Tu backlog se está reduciendo ({{resolved}} resueltos vs {{created}} nuevos al día).',
    capacityHealthy: 'La capacidad es saludable. Tu equipo mantiene un ritmo constante de resolución.',
    generateFullReport: 'Generar Reporte Completo',
    tasksInQueue: 'tareas actualmente en cola',
    milestonesReached: 'hitos alcanzados',
    avgTimeTriage: 'Tiempo promedio desde triage hasta completado',
    activeResolutionDays: 'Días con resolución activa (últimos 14d)',
    seeAll: 'Ver detalles',
    workloadDistribution: 'Distribución de Carga',
    efficiencyFactor: 'Factor de Eficiencia',
    predictedFinish: 'Fin Estimado',
    noDataTitle: 'Análisis Pendiente',
    noDataDesc: 'Empieza a rastrear tickets para ver métricas en tiempo real.',
    noActiveBacklogDesc: 'Este proyecto está configurado correctamente, pero aún no tiene tickets registrados. Comienza a rastrearlos instalando el wrapper en tu aplicación.',
    backlogHealthy: '¡Al día! Tu equipo tiene el backlog perfectamente gestionado.',
    goToBacklog: 'Ir al Backlog',
    changeProject: 'Cambiar Proyecto',
    teamHealth: 'Salud del Equipo',
    owner: 'Creador',
    creatorLabel: 'Creador',
    resolver: 'Resuelto por',
    plannedBy: 'Planificado por',
    priority: 'Prioridad',
    created: 'Creado',
    descriptionPlaceholder: 'Ingresa una descripción o comentario...',
    noDescription: 'No se proporcionó información adicional.',
    originalComment: 'Comentario Original',
    noOriginalMessage: 'Mensaje original no disponible.',
    newTicket: 'Nuevo Ticket',
    invitationTitle: 'Invitación al Proyecto',
    invitationDesc: 'Has sido invitado a colaborar en',
    acceptInvitation: 'Aceptar Invitación',
    joinProject: 'Unirse al Proyecto',
    invitationAccepted: '¡Te has unido al proyecto con éxito!',
    invitationEmailMismatch: 'Esta invitación es para {email}, pero has iniciado sesión como {currentUser}.',
    switchAccount: 'Cambiar de Cuenta',
    invalidInvitation: 'Esta invitación no es válida o ha expirado.',
    loginToAccept: 'Por favor, inicia sesión para aceptar la invitación',
    redirectingToProjects: 'Redirigiendo a tus proyectos...',
    joiningAs: 'Uniéndote como',
    loginToViewGate: 'Iniciar sesión en ViewGate',
    cancelInvitation: 'Cancelar Invitación',
    confirmCancelInvitation: '¿Estás seguro de que quieres cancelar esta invitación?',
    removeMember: 'Eliminar Miembro',
    confirmRemoveMember: '¿Estás seguro de que quieres eliminar a este miembro del proyecto?',
    pending: 'Pendiente',
    role: 'Rol',
    role_owner: 'Project Manager',
    role_role_owner: 'Project Manager',
    role_client: 'Cliente',
    role_pm_desc: 'Gestión total del proyecto y equipo',
    role_ux_ui_desc: 'Comentarios visuales y diseño',
    role_dev_desc: 'Implementación y detalles técnicos',
    role_qa_desc: 'Pruebas y reporte de errores',
    role_client_desc: 'Solo lectura y backlog',
    inviteToProject: 'Invita un colaborador a {{name}}',
    selectMemberRole: 'Seleccionar rol del miembro',
    sendInvitation: 'Enviar Invitación',
    permissionDenied: 'Permiso Denegado: No tienes acceso a esta sección.',
    edit: 'Editar',
    description: 'Descripción/comentario',
    close: 'Cerrar',
    urgent: 'Urgente',
    noProjectsTitle: '¡Bienvenido a ViewGate!',
    noProjectsDesc: 'Comienza creando tu primer proyecto o esperando una invitación.',
    createFirstProject: 'Crear Primer Proyecto',
    high: 'Alta',
    medium: 'Media',
    low: 'Baja',
    ready_for_review: 'Revisión',
    completed: 'Completado',
    liveDashboard: 'Panel en Vivo',
    project: 'Proyecto',
    status: 'Estado',
    createdLabel: 'Creado',
    resolvedLabel: 'Resuelto',
    daysLetter: 'd',
    admin: 'Admin',
    untitled: 'Sin Título',
    selectOption: 'Seleccionar opción...',
    noOptions: 'No hay opciones disponibles',
    components: 'Componentes',
    componentsDesc: 'Crea y gestiona componentes UI desde links de Figma. La generación y previews se actualizan progresivamente.',
    createComponent: 'Crear Componente',
    componentsList: 'Lista de Componentes',
    figmaUrl: 'URL de Figma',
    figmaUrlPlaceholder: 'Pega un link de componente de Figma...',
    figmaLink: 'links de figma',
    zipUpload: 'carga de zip',
    zipFile: 'Archivo ZIP',
    zipError: 'El archivo ZIP debe contener al menos un archivo .html y uno .css.',
    componentType: 'Tipo de Componente',
    requiredProps: 'Props obligatorias',
    noComponentsYet: 'Aún no hay componentes creados.',
    noPreview: 'Sin preview todavía',
    generated: 'Generado',
    
    // E2E Testing
    e2eTestFlows: 'Flujos E2E',
    e2eTestFlowsDesc: 'Tests end-to-end generados automáticamente de interacciones de usuario',
    e2eTotalFlows: 'Total de Flujos',
    e2eActive: 'Activos',
    e2eReadyForCICD: 'Listos para CI/CD',
    e2eNeedsReview: 'Necesitan Revisión',
    e2eRequiresAttention: 'Requieren atención',
    e2eAvgConfidence: 'Confianza Promedio',
    e2eSelectorReliability: 'Confiabilidad de selectores',
    e2eTestFlowList: 'Flujos de Test',
    e2eFlows: 'flujos',
    e2eRefresh: 'Actualizar',
    e2eNoProjectSelected: 'Ningún proyecto seleccionado',
    e2eSelectProjectDesc: 'Selecciona un proyecto de la barra lateral para ver y gestionar tus flujos E2E.',
    e2eNoFlowsYet: 'Aún no hay flujos de test',
    e2eNoFlowsDesc: 'Usa el wrapper de ViewGate para grabar interacciones de usuario. Haz clic en el icono del matraz en tu app para comenzar a grabar.',
    e2eClickBeakerHint: 'Haz clic en el icono del matraz en tu app',
    e2eExport: 'Exportar',
    e2eDelete: 'Eliminar',
    e2eSteps: 'Pasos',
    e2eLastUpdated: 'Última actualización',
    e2eCreated: 'Creado',
    e2eStepsCount: 'pasos',
    e2eConfidence: 'confianza',
    e2eTestSteps: 'Pasos de Test',
    e2eStepsTotal: 'pasos en total',
    e2eLowConfidence: 'Confianza baja',
    e2eDeleteTitle: 'Eliminar Flujo de Test',
    e2eDeleteDescription: 'Esta acción no se puede deshacer. El flujo de test será eliminado permanentemente de tu proyecto.',
    e2eExportTitle: 'Exportar Test',
    e2eExportReady: 'Listo para usar en tu pipeline de CI/CD',
    e2eCopyClipboard: 'Copiar al Portapapeles',
    e2eCopied: '¡Copiado!',
    e2eClose: 'Cerrar',
    e2eNoCode: 'Código no disponible',
    
    create: 'Crear',
    addFile: 'Agregar archivo',
    dropHere: 'Soltar aquí',
    dragDropInfo: 'Arrastra y suelta tu archivo ZIP aquí',
    orClickToBrowse: 'o haz clic para buscar archivos',
    fileReady: 'Archivo listo para subir',
    downloadPDF: 'Descargar Reporte PDF',
    executiveReport: 'Reporte Ejecutivo del Proyecto',
    printReport: 'Imprimir / Exportar PDF',
    ticketsResolvedTotal: 'tickets resueltos en total',
    transferSuccess: 'Propiedad Transferida',
    transferSuccessDesc: 'La propiedad se ha transferido correctamente. Ya no tienes acceso a este proyecto.',
    transferError: 'Error de Transferencia',
    errorTransferringOwnership: 'No se pudo transferir la propiedad del proyecto.',
    transferPM: 'Transferir Gestión',
    designateAsPM: 'Designar como PM',
    transferWarningTitle: 'Transferir Propiedad del Proyecto',
    transferWarningDesc: 'Estás a punto de designar a {{email}} como el nuevo Project Manager. Una vez confirmado, serás eliminado del equipo y perderás todo el acceso. Esta acción no se puede deshacer.',
    confirmTransfer: 'Confirmar Transferencia',

    efficiencyRate: 'Tasa de Eficiencia',
    activeBacklog: 'Backlog Activo',
    pendingActionItems: 'temas con acción pendiente',
    velocityTrends: 'Tendencias de Velocidad y Capacidad',
    highPriorityAttention: 'Temas Críticos que Requieren Atención',
    noHighPriorityInsights: 'No se detectaron temas de alta prioridad en este momento.',
    projectHealthInsights: 'Información Ejecutiva del Proyecto',
    reportBacklogWarning: 'Tu backlog se encuentra actualmente significativamente por encima de los niveles óptimos.',
    reportCapacityHealthy: 'La capacidad operativa es estable y las resoluciones son constantes.',
    reportStabilityInsight: 'La estabilidad del proyecto se mantiene dentro de los parámetros de gestión.',
    toConfirmWrite: 'Para confirmar, escribe',
    confirmType: 'Escribe "{name}" para confirmar',
    noWorkspaceBacklog: 'Crea un proyecto para empezar a gestionar tu backlog.',
    noProjectsInWorkspace: 'No hay proyectos creados todavía',
    noProjectsInWorkspaceDesc: 'Crea un proyecto para empezar a gestionar tus sprints y backlog.',
    newSprintDescription: 'Planifica tu próximo ciclo de trabajo con un nuevo sprint.',
    sprintPlaceholder: 'Sprint #1',
    sprints: 'Sprints',
    confirmed: 'Confirmado',
    slide: 'Desliza',
    backlogItems: 'Ítems del Backlog',
    visualReference: 'Referencia Visual',
    fetchingEvidence: 'Obteniendo evidencia...',
    highResCapture: 'Captura de alta resolución disponible',
    createSprint: 'Crear Sprint',
    sprintCreated: 'Sprint creado con éxito',
    projectCreated: 'Proyecto creado con éxito',
    workspaceCreated: 'Espacio de trabajo creado con éxito',
    sprintName: 'Nombre del Sprint',
    sprintDuration: 'Duración (Días)',
    noSprints: 'No hay sprints creados aún',
    noSprintsAvailable: 'No hay sprints disponibles',
    addToSprint: 'Añadir al Sprint',
    activeSprint: 'Sprint Activo',
    plannedSprints: 'Sprints Planeados',
    sprintDays: 'Días',
    startDate: 'Fecha de inicio',
    endDate: 'Fecha de fin',
    day: 'día',
    days: 'días',
    backlogSprints: 'Backlog y Sprints',
    commitmentCapacity: 'Capacidad de Compromiso',
    sprintItems: 'Ítems del Sprint',
    availableInBacklog: 'Disponible en el Backlog',
    noItemsInSprint: 'Sin ítems en este sprint',
    confirmDeleteSprint: 'Eliminar Sprint',
    confirmDeleteSprintMsg: 'Esta acción no se puede deshacer. Todos los tickets asignados volverán al backlog.',
    confirmDeleteHistorySprintMsg: 'Esta acción no se puede deshacer. Todos los tickets asociados se eliminarán permanentemente del sistema.',
    closeSprint: 'Cerrar Sprint',
    confirmCloseSprint: 'Cerrar Sprint Activo',
    confirmCloseSprintMsg: '¿Estás seguro de que quieres cerrar este sprint? Los tickets resueltos quedarán en el historial y los pendientes pasarán al próximo sprint planeado.',
    confirmRemoveFromSprint: 'Quitar del Sprint',
    confirmRemoveTicketMsg: 'Este ticket volverá al backlog general. ¿Estás seguro?',
    deselectSprint: 'Deseleccionar Sprint',
    selectSprintToConfirm: 'Selecciona un Sprint',
    confirm: "Confirmar",
    evidenceComparison: 'Comparación de Evidencia',
    evidenceBefore: 'Evidencia Inicial',
    evidenceAfter: 'Evidencia Resuelta',
    comparisonView: 'Comparación de Cambios',
    beforeLabel: 'Antes',
    afterLabel: 'Después',
    noAfterEvidence: 'Aún no se ha capturado evidencia de resolución.',
    resolveWithCapture: 'Resolver y Capturar',
    viewFull: 'Ver Pantalla Completa',

    confirmDeleteTicket: 'Eliminar Ticket',
    confirmDeleteTicketMsg: '¿Estás seguro de que deseas eliminar permanentemente el ticket {id}? Esta acción no se puede deshacer.',
    movedToBacklog: "Movido al Backlog",
    movedToNextSprint: "Movido al siguiente sprint",
    waitingForNextSprint: "Esperando al siguiente Sprint",
    movedTo: "Movido a",
    ticketResolved: "Ticket Resuelto",
    deleted: 'Eliminado',
    deletedAt: 'Eliminado el',

    // New strings from scan
    apiAccessKeys: 'Tokens de Acceso SDK',
    apiAccessKeysDesc: 'Utiliza estos tokens para autorizar el tráfico del SDK hacia ViewGate. Recomendamos aplicar restricciones de origen (CORS) en entornos productivos para máxima seguridad.',
    keyPrefix: 'Prefijo de la Clave',
    action: 'Acción',
    registerWebhook: 'Registrar nuevo endpoint de Webhook',
    sdkReference: 'Referencia del SDK',
    mcpConfiguration: 'Configuración de MCP',
    securityWhitepaper: 'Libro Blanco de Seguridad',
    systemStatusOperational: 'Estado del Sistema: Operativo',
    sharedVsPersonalKeys: 'Claves Compartidas vs Personales',
    sharedVsPersonalKeysDesc: 'Las claves de equipo requieren un actorId para la trazabilidad. Las claves personales se mapean directamente a tu cuenta de usuario.',
    noMembers: 'Aún no hay miembros.',
    emailAddress: 'Dirección de Correo',
    inviteDesc: 'Ingresa el correo Gmail de la persona que quieres invitar a **{name}**.',
    planned_status: 'Planificado',
    moved_status: 'Movidos',
    loading: 'Cargando',
    authenticating: 'Autenticando...',
    ok: 'OK',
    error: 'Error',
    errorCreatingProject: 'No se pudo crear el proyecto. Por favor, intenta de nuevo.',
    keyError: 'Error de Clave',
    errorGeneratingKey: 'No se pudo generar la clave API. Verifica los permisos.',
    errorDeletingKey: 'No se pudo eliminar la clave API.',
    cannotDeleteLastKey: 'No puedes eliminar la clave predeterminada si no existen otras claves de respaldo. Crea una nueva primero.',
    errorInvitingMember: 'No se pudo invitar al miembro.',
    invitationError: 'Error de Invitación',
    invitationSent: 'Invitación Enviada',
    invitationSentDesc: 'Se ha enviado un correo a {{email}} para unirse al proyecto.',
    alreadyMember: 'El usuario ya es miembro de este proyecto.',
    tier_limit_reached: 'Límite del Plan Alcanzado',
    pm_limit_reached: 'Límite de Project Managers Alcanzado',
    pmLimitNote: 'Solo se permiten 2 Project Managers por proyecto.',
    pmLimitReachedShort: 'Límite: 2',
    deletionError: 'Error de Eliminación',
    errorDeletingProject: 'Hubo un error al intentar eliminar el proyecto.',
    allOrigins: 'Todos los Orígenes',
    projectNamePlaceholder: 'ej. Sitio Web Acme, ERP Interno...',
    keyNamePlaceholder: 'ej. Servidor Staging, CRM Producción...',
    originExample: 'ej. localhost:5173',
    understood: 'Entendido',
    revokeKeyConfirm: '¿Estás seguro de que quieres revocar esta clave API? Esta acción no se puede deshacer.',
    failedRevokeKey: 'Error al revocar la clave',
    more: 'más',
    loadMore: 'Cargar más',

    // Toast Feedback
    toastError: 'Ha ocurrido un error inesperado al procesar la solicitud.',
    toastSprintDeleted: 'Sprint eliminado correctamente. Los tickets han vuelto al backlog.',
    toastSprintOverlapError: 'No se pudo crear el sprint: Las fechas seleccionadas coinciden con otro sprint activo o planeado.',
    toastTicketAdded: 'Ticket asignado al sprint con éxito.',
    toastTicketRemoved: 'Ticket retirado del sprint y movido al backlog general.',
    toastTicketDeleted: 'El ticket ha sido eliminado permanentemente del sistema.',
    toastSprintClosed: 'Sprint finalizado exitosamente. El resumen ha sido generado.',
    toastHistorySprintDeleted: 'Sprint eliminado correctamente del historial.',
    toastMemberRemoved: 'Miembro eliminado con éxito.',
    toastInvitationCancelled: 'Invitación cancelada con éxito.',
    aiFixSummary: 'Resumen de Arreglo (IA)',
    aiFixSummaryDesc: 'Resumen de los cambios aplicados por la IA',
    aiVerified: 'Verificado por IA',

    // Planning
    planningAI: 'Planificación IA',
    complexity: 'Complejidad',
    uncertainty: 'Incertidumbre',
    impact: 'Impacto',
    score: 'Puntaje',
    aiAnalysis: 'Análisis de la IA',
    type: 'Tipo',
    totalPoints: 'Puntos totales',
    predictedCapacity: 'Capacidad estimada',
    capacity: 'Capacidad',
    planningRequiredToSprint: 'Planificación Requerida: Este ticket debe ser analizado por la IA antes de ser añadido a un sprint.',
    planningRequiredTitle: 'Planificación Requerida',
    aiFriendly: 'Fácil para IA',
    aiAssisted: 'Asistido por IA',
    humanCritical: 'Crítico (Humano)',

    // Tooltips
    complexityTooltip: '1: Simple (UI/texto)\n2: Lógica moderada\n3: Compleja (arquitectura/integraciones)',
    uncertaintyTooltip: '1: Definido\n2: Ambiguo\n3: Muy poco claro',
    impactTooltip: '1: Menor\n2: Útil\n3: Crítico (negocio/core)',
    riskTooltip: '1: Bajo (sin dep)\n2: Moderado\n3: Alto (multi-dep/crítico)',
    scoreTooltip: 'Esfuerzo técnico\n(Complejidad² + Incertidumbre + Riesgo)',
    priorityTooltip: 'Urgencia relativa\n(Impacto / Puntaje)',
    typeTooltip: 'Aptitud de la IA para una resolución autónoma',
    sprintOccupied: 'Fecha ocupada',
    sprintOverlapWarning: 'Solapamiento con',
    noSearchResults: 'Sin resultados',
    noSearchResultsDesc: 'No pudimos encontrar tickets que coincidan con "{{query}}". Intenta con otro término o limpia la búsqueda.',
    emptyBacklogTitle: '¡Backlog Despejado!',
    emptyBacklogMessage: 'Todos los tickets han sido planificados o resueltos. Tu proyecto está al día.',
    noResultsTitle: 'Sin coincidencias',
    emptySprintTitle: 'Sprint Vacío: No hay items asignados',
    noResultsMessage: 'Refina tu búsqueda o revisa la ortografía.',
    loadingHistory: 'Cargando historial...',
    toastSettingsSaved: "Configuración guardada correctamente",

    // Modales de Documentación
    installation: 'Instalación',
    basicSetup: 'Configuración Básica',
    sdkSetupDesc: 'Envuelve tu componente principal con el proveedor de ViewGate para habilitar la captura de UI y logs en tiempo real.',
    newFeatures: 'Nuevas Funciones',
    searchableEndpoints: 'Endpoints Buscables',
    searchableEndpointsDesc: 'Vincula tu lógica de backend con elementos de la UI usando el nuevo selector con búsqueda directamente en el wrapper.',
    dynamicFocusAreas: 'Áreas de Foco Dinámicas',
    dynamicFocusAreasDesc: 'El wrapper ahora oculta automáticamente las áreas de Backend y UX UI si no están configuradas o conectadas.',
    autoCapture: 'Captura Automática',
    autoCaptureDesc: 'Rastrea cambios en la UI y los correlaciona con tu código usando source maps automáticamente.',
    offlineReady: 'Listo para Offline',
    offlineReadyDesc: 'Encola el feedback localmente y lo sincroniza automáticamente cuando se restaura la conexión.',
    mcpIntro: 'El Protocolo de Contexto de Modelo (MCP) permite que ViewGate se comunique con tu asistente de IA para realizar ediciones de código nativas con protección de concurrencia y bloqueo de tickets.',
    localizedIntelligence: 'Inteligencia quirúrgica con trazabilidad completa de IA.',
    npmInstall: 'Paso 1: Instalar Paquete',
    npmInstallDesc: 'Instala el paquete base del wrapper en tu proyecto.',
    step2UnifiedSetup: 'Paso 2: Configuración Unificada',
    step2UnifiedSetupDesc: 'Este comando configura tu .env y conecta las herramientas MCP a tu proyecto.',
    step3Configuration: 'Paso 3: Agregar Clave API',
    mcpConfigDesc: "Abre la configuración de MCP de tu IDE y pega tanto la Clave API del proyecto como tu Clave Personal del dashboard en el archivo de configuración generado para una trazabilidad completa de la IA.",
    discard: 'Descartar',
    stay: 'Permanecer',
    save: 'Guardar',
    availableTools: 'Herramientas Disponibles',
    syncEndpointsDesc: 'Escaneo automático y sincronización por lotes de alto rendimiento de tus rutas de API.',
    getAnnotationsDesc: 'Obtén instrucciones de corrección quirúrgica con Bloqueo de Tickets (1h) para evitar colisiones.',
    planningDesc: 'Analiza el backlog con evaluación de complejidad y riesgo impulsada por IA.',
    markReadyDesc: 'Actualiza tickets con resúmenes de IA. Soporta procesamiento por lotes y reintentos automáticos.',
    keyBenefits: 'Beneficios Clave',
    automatedBugFixing: 'Corrección Automática de Errores',
    contextAwareRefactoring: 'Refactorización con Contexto',
    directUiToCodeSync: 'Sincronización Directa UI-Código',
    aiDrivenPlanning: 'Planificación de Sprints con IA',
    capacityPrediction: 'Predicción Automática de Capacidad',
    dataPrivacy: 'Privacidad de Datos',
    dataPrivacyDesc: 'ViewGate nunca almacena tu código completo. Solo capturamos snapshots de UI y metadatos necesarios para ubicar componentes.',
    compliance: 'Cumplimiento',
    complianceDesc: 'Nuestra infraestructura cumple con SOC2 Tipo II. Todos los datos se cifran en tránsito (TLS 1.3) y en reposo (AES-256).',
    securityArchitecture: 'Arquitectura de Seguridad',
    isolatedWorkspace: 'Espacio de Trabajo Aislado',
    isolatedWorkspaceDesc: 'Cada proyecto se ejecuta en un contenedor aislado criptográficamente.',
    rbacAuth: 'Autorización RBAC',
    rbacAuthDesc: 'Control de acceso granular para visualizar capturas de código.',
    sourceTraceability: 'Trazabilidad de Origen',
    sourceTraceabilityDesc: 'Cada edición de IA está firmada y es rastreable hasta una sesión específica.',
    versionStable: 'Última Versión • 2026 Estable',
    needHelpSecurity: '¿Necesitas ayuda? Contacta a nuestro equipo en',
    fullDocs: 'docs completas',

    // Sprint Planning
    initiatePlanning: 'Iniciar Planificación',
    lockCapacity: 'Bloquear Capacidad',
    suggestNext: 'Sugerir Siguiente',
    capacityMeter: 'Medidor de Capacidad',
    targetCapacity: 'Capacidad Objetivo',
    empiricalCapacity: 'Capacidad Empírica',
    uncertaintyFactor: 'Factor de Incertidumbre',
    teamEfficiency: 'Eficiencia del Equipo',
    backlogQuality: 'Calidad del Backlog',
    planningPhase: 'Fase de Planificación',
    buildPhase: 'Fase de Construcción',
    lockPhase: 'Fase de Cierre',
    draft: 'Borrador',
    planning: 'Planificación',
    safe: 'Seguro',
    warning: 'Advertencia',
    overflow: 'Desbordamiento',
    points: 'Puntos',
    remaining: 'Restante',
    totalCapacity: 'Capacidad Total',
    suggestNextDesc: 'Busca el mejor ticket basado en prioridad y capacidad restante.',
    lockPlanningDesc: 'Finaliza la capacidad y pasa a la fase de construcción.',
    lockPlanningDescWithPoints: 'Tu equipo comenzará este sprint con una capacidad estimada de {points} puntos para {days} días. Finaliza la planificación para pasar a la fase de construcción.',
    capacityCalculated: 'Capacidad Calculada',
    uncertaintyPenalty: 'Penalización por Incertidumbre',
    suggestionFound: '¡Sugerencia encontrada!',
    noCompatibleTickets: 'No se encontraron tickets compatibles con la capacidad restante.',
    spanish: 'Español',
    english: 'Inglés',
    sprintItemDeleted: 'Ítem del sprint eliminado',
    confirmSignOut: 'Confirmar Cierre de Sesión',
    confirmSignOutMsg: '¿Estás seguro de que deseas cerrar la sesión?',
    transferConfirmation: 'Confirmar Transferencia',
    delete: 'Eliminar',
    confirmDeleteComponentTitle: '¿Eliminar componente?',
    confirmDeleteComponentMsg: 'Esto eliminará permanentemente "{name}" de este proyecto. Esta acción no se puede deshacer.',
    figmaTokenDesc: 'Centraliza las referencias de Figma para permitir que el MCP local del desarrollador proporcione contexto de diseño automatizado.',
    endpointPath: 'Ruta',
    endpointMethod: 'Método',
    focusDetails: 'Detalles de Enfoque',
    personalKey: 'Clave Personal',
    personalKeyDesc: 'Esta clave te identifica al resolver tickets mediante el MCP. Manténla en privado.',
    weeklyAccessKey: 'Clave de Acceso Semanal',
    weeklyKeyDesc: 'Esta clave es válida por 7 días e identifica a tu usuario dentro de este proyecto específico. Úsala para autenticar el Wrapper de ViewGate en tu entorno.',
    generateWeeklyKey: 'Generar Clave Semanal',
    weeklyKeyGenerated: 'Clave Semanal generada con éxito',
    appliedLocally: 'Aplicado Localmente',
    pendingDeploy: 'Pendiente de Deploy',
    markAsLive: 'Marcar como desplegado',
    aiApplied: 'IA Aplicada',
    generatePersonalKey: 'Generar Clave Personal',
    regeneratePersonalKey: 'Regenerar Clave Personal',
    personalKeyGenerated: 'Clave Personal generada con éxito',
    copyKey: 'Copiar Clave',
    role_pm: 'Project Manager',
    role_dev: 'Desarrollador',
    role_ux_ui: 'Diseñador UX UI',
    role_qa: 'Ingeniero QA',
    moveToReview: 'Pasar a Revisión',
    returnToPending: 'Volver a Pendientes',
    returnToBugs: 'Volver a Errores',
    completeTicket: 'Completar Ticket',
    confirmStatusTitle: 'Confirmar Cambio de Estado',
    confirmStatusMessage: '¿Estás seguro de que quieres cambiar el estado de este ticket a {status}?',
    affectedEndpoints: 'Endpoints Afectados',
    uxReferences: 'Referencias UX',
    copied: '¡Copiado!',
    sprintHealth: 'Salud del Sprint',
    sprintStatus: 'Estado Sprint',
    healthy: 'Saludable',
    risk: 'En Riesgo',
    critical: 'Crítico',
    pointLoad: 'Carga de Puntos',
    sprintStarting: 'Sprint Iniciando',
    progress: 'Progreso',
    timeElapsed: 'Tiempo Transcurrido',
    healthBehindSchedule: 'Atraso crítico en el cronograma',
    healthTooManyUrgent: 'Múltiples tickets urgentes pendientes',
    healthBugOverflow: 'Sobrecarga de errores',
    healthSlightlyBehind: 'Progreso por debajo del ideal',
    healthUrgentPending: 'Tickets urgentes pendientes',
    healthHighWipBugs: 'WIP alto en resolución de bugs',
    healthHealthy: 'Sprint saludable y en ritmo',
    externalIntegrations: 'Integraciones Externas',
    syncToJira: 'Sincronizar Sprint a Jira',
    syncToAzure: 'Sincronizar Sprint a Azure',
    syncInProgress: 'Sincronizando tickets...',
    syncSuccess: 'Tickets sincronizados con éxito',
    importantNote: 'IMPORTANTE',
    evidenceBypassWarning: 'Advertencia: Al completar el ticket desde aquí no se generará la captura de evidencia del "Después". Para incluirla, utiliza el botón "Resolver con Evidencia" en el wrapper.',
    requiresCorrections: 'Correcciones Necesarias',
    correctionsRequired: 'Correcciones Requeridas',
    correctionsDesc: 'Por favor, detalla las correcciones necesarias para el desarrollador.',
    correctionsPlaceholder: 'Escribe aquí tu feedback...',
    correctionHistory: 'Historial de Correcciones',
    deleteCorrectionTitle: 'Eliminar Corrección',
    confirmDeleteCorrection: '¿Estás seguro de que deseas eliminar permanentemente esta corrección? Esta acción no se puede deshacer.',
    fixDescriptionLabel: '¿Cómo resolviste esto?',
    fixDescriptionPlaceholder: 'Describe los cambios y correcciones realizadas...',
    pendingFix: 'Corrección Pendiente',
    resolvedFix: 'Corregido',
    appliedFix: 'Aplicada por IA',
    appliedByAI: 'Aplicada por Agente IA',
    correctionsMandatory: 'El feedback es obligatorio para retroceder',
    sendCorrections: 'Enviar y Retroceder Ticket',
    iaCommentWarning: 'Al pasar a revisión desde el tablero, no se generará el comentario automático de la IA sobre los cambios aplicados. Para incluirlo, resuelve el ticket directamente desde tu IDE usando el MCP.',
    uploadManualEvidence: "Subir Evidencia Manual",
    uploadManualEvidenceDesc: "Sube una captura de pantalla para validar los cambios realizados.",
    uploadingEvidence: "Subiendo...",
    uploadSuccess: "Evidencia subida correctamente",
    uploadError: "Error al subir la evidencia",
    selectImage: "Seleccionar Imagen",
    validatedEvidence: "Evidencia Validada",
    active: "Activo",
    filtered: "Filtrado",
    integrationDesc: "Conecta tu espacio de trabajo con servicios de terceros para automatizar tu flujo.",
    figmaConnected: "Conexión con Figma",
    figmaDesc: "Sincroniza tus tokens de diseño y componentes directamente desde archivos de Figma.",
    jiraConfig: "Configuración de Jira Cloud",
    azureConfig: "Configuración de Azure DevOps",
    serverUrl: "URL del Servidor",
    apiToken: "Token de API",
    projectKey: "Clave del Proyecto",
    orgUrl: "URL de la Organización",
    enabled: "Habilitado",
    requiredFieldsMissing: "Faltan campos requeridos",
    developerSettings: "Configuración",
    settingsDesc: "Configura los endpoints de tu proyecto, capacidades e integraciones externas.",
    sprintSettings: "Capacidad y Planificación",
    capacityDesc: "Configura la velocidad del equipo y la carga de trabajo permitida para el Agente AI.",
    aiAgentSettings: "Configuración del Agente AI",
    aiAgentSettingsDesc: "Gestione la capacidad operativa y el comportamiento del Agente MCP.",
    defaultCapacity: "Capacidad Diaria por Defecto",
    sprintLimit: "Capacidad del Agente AI",
    sprintLimitDesc: "Cantidad máxima de tickets simultáneos que el agente puede procesar.",
    aiLanguage: "Idioma de Análisis",
    aiLanguageDesc: "Selecciona el idioma principal para los comentarios e insights de la IA.",
    testingFramework: "Framework de Testing",
    testingFrameworkDesc: "Framework de testing por defecto para generar flujos E2E a partir de interacciones capturadas.",
    e2eMCPProcessed: 'Procesado IA',
    e2eCaptured: 'Capturado',
    backendEndpoints: "Endpoints del Backend",
    endpointsDesc: "Gestiona y documenta tus rutas de API para el wrapper de ViewGate.",
    routes: 'Rutas',
    lastSynced: 'Última Sincronización',
    noResults: "No hay endpoints que coincidan con tu búsqueda.",
    saveSettings: "Guardar Configuración",
    unsavedChanges: "Cambios sin Guardar",
    unsavedChangesDesc: "Has modificado la configuración. Si sales ahora, se perderán estos cambios.",
    documentation: "Documentación Técnica",
    documentationDesc: "Aprende cómo integrar ViewGate en tu ciclo de vida de desarrollo.",
    branding: 'Branding Personalizado',
    val_3_per_day: '3 por día',
    platinum_level: 'Nivel Platinum',
    elevate_team: 'Eleva tu equipo a',
    platinum_desc: 'Desbloquea límites más altos, histórico completo de backlog y la inteligencia avanzada de nuestro Scrum Expert desde $10.000 ARS por miembro.',
    active_billing_members: 'miembros del equipo facturables',
    monthly_cost_label: 'Inversión Mensual',
    subscription_disclaimer: 'Suscripción mensual recurrente. Puedes gestionar o cancelar en cualquier momento desde tu panel de Mercado Pago.',
    privacy_guaranteed: 'Privacidad Garantizada',
    privacy_desc: 'Tus capturas y datos están encriptados. No vendemos información de tu empresa. Privacidad nivel militar.',
    free_observers: 'Observadores Gratis',
    free_observers_desc: 'Solo pagas por los que "hacen". Project Managers y Clientes tienen acceso total de lectura sin costo adicional.',
    faq_title: 'Preguntas Frecuentes',
    faq_q2: '¿Qué pasa si mi suscripción vence o es gratuita?',
    faq_a2: 'Los espacios de trabajo gratuitos están limitados a 5 usuarios únicos, 3 proyectos, 15 capturas por día y 100MB de almacenamiento.',
    faq_q3: '¿Es seguro el proceso de pago?',
    faq_a3: 'Absolutamente. Utilizamos Mercado Pago para procesar todos los pagos con encriptación de nivel bancario y máxima seguridad.',
    partnership_label: 'Infraestructura Respaldada por',
    enterprise_plan_q: '¿Escalando un equipo de alto rendimiento?',
    enterprise_plan_a: 'Para organizaciones de más de 50 usuarios: Facturación A, SLAs corporativos y soporte prioritario de ingeniería 24/7.',
    dailyCaptures: 'Capturas Diarias',
    backlogAccess: 'Acceso al Backlog',
    scrumExpert: 'Scrum Expert',
    integrations_label: 'Integraciones',
    active_subscription: 'Suscripción Activa',
    contact_support: 'Contactar Soporte',
    elevate_workspace: 'Potencia tu Espacio de Trabajo',
    workspace_billing_desc: 'Los espacios de trabajo te permiten agrupar múltiples proyectos bajo una única suscripción. Tu plan cubre todos los proyectos dentro del espacio activo.',
    unique_billable_users: 'Usuarios Únicos Facturables',
    upgrade_workspace_cta: 'Mejorar Espacio',
    faq_q1: '¿Cómo se calcula el costo?',
    faq_a1: 'Contamos cada miembro activo de tu espacio de trabajo (PMs, Desarrolladores, Diseñadores, QA). El precio depende de tu nivel ($10k ARS para Pro, $25k para Enterprise) multiplicado por el equipo total.',
    faq_q4: '¿Cómo funciona el Plan Enterprise?',
    faq_a4: 'Está diseñado para equipos grandes que necesitan límites personalizados, SLAs dedicados y soporte prioritario 24/7. Contáctanos para un plan a medida.',
    support_billing: 'Soporte y Facturación',
    still_have_questions: '¿Aún tienes dudas?',
    response_time_2h: 'Respuesta en <span class="text-emerald-500">menos de 2h</span>',
    no_workspaces_found: 'No se encontraron espacios',
    only_pm_manage_sub: 'Las suscripciones solo pueden ser gestionadas por el Project Manager que creó el espacio. Actualmente no eres dueño de ningún espacio de trabajo.',
    payment_confirmed: 'Pago Confirmado',
    payment_pending: 'Pago en Proceso',
    workspace_updated_success: '¡Tu espacio de trabajo ha sido actualizado! Disfruta de tus nuevos beneficios.',
    processing_subscription_wait: 'Estamos procesando tu suscripción. Se activará en unos segundos.',
    contactFormTitle: 'Contactar Soporte',
    contactFormDesc: 'Envíanos un mensaje y te responderemos a la brevedad.',
    submitConsultation: 'Enviar Consulta',
    consultationSuccess: 'Consulta Enviada con Éxito',
    redirectingSoon: 'Cerrando ventana...',
    subject: 'Asunto',
    message: 'Mensaje',
    email_placeholder: 'tu@email.com',
    subject_placeholder: 'Resumen breve de tu consulta...',
    subject_support: 'Consulta de Soporte',
    subject_sales: 'Consulta sobre Plan Enterprise',
    message_placeholder: 'Escribe aquí los detalles de tu consulta...',
    secure_intake_protocol: 'Protocolo de Ingreso Seguro ViewGate v2.5',
    redirecting_to_mercadopago: 'Redireccionando a Mercado Pago...',
    verifying_payment: 'Verificando con la pasarela de pago...',

    // Workspaces & Tiers
    workspaces: 'Espacios de Trabajo',
    workspace_name: 'Nombre del Espacio',
    create_workspace: 'Crear Espacio',
    manage_workspaces: 'Gestionar Espacios',
    switch_workspace: 'Cambiar de Espacio',
    no_workspaces: 'No se encontraron espacios',
    new_workspace: 'Nuevo Espacio de Trabajo',
    workspace_settings: 'Ajustes del Espacio',
    plan_founders: 'Plan Founders',
    plan_free: 'Nivel Gratuito',
    plan_pro: 'Plan Pro',
    plan_enterprise: 'Plan Enterprise',
    plan_pro_desc: 'Desbloquea límites más altos, histórico completo de backlog e inteligencia avanzada.',
    plan_enterprise_desc: 'Soluciones a medida para equipos grandes, branding personalizado y soporte dedicado.',

    // Limits
    limit_5_users: '5 Usuarios Únicos',
    limit_20_users: '20 Usuarios Únicos',
    limit_50_users: '50 Usuarios Únicos',
    limit_unlimited_users: 'Usuarios Ilimitados',
    limit_3_projects: '3 Proyectos',
    limit_10_projects: '10 Proyectos',
    limit_50_projects: '50 Proyectos',
    limit_unlimited_projects: 'Proyectos Ilimitados',
    limit_15_screenshots: '15 Capturas / Día',
    limit_100_screenshots: '100 Capturas / Día',
    limit_500_screenshots: '500 Capturas / Día',
    limit_100mb: '100MB almacenamiento',
    limit_2gb: '2GB almacenamiento',
    limit_10gb: '10GB almacenamiento',
    active_subscription_calc: 'Suscripción Activa',
    subscription_disclaimer_calc: 'Facturación mensual escalable. Cancela o cambia de plan en cualquier momento.',

    // Comparison Table
    plans_comparison: 'Comparar Planes',
    level_comparison: 'Encuentra el nivel ideal para tu equipo',
    screenshots_limit: 'Capturas por Día',
    total_projects: 'Proyectos Totales',
    storage_limit: 'Cuota de Espacio',
    unique_users: 'Miembros del Equipo',
    unlimited: 'Ilimitado',
    recommended: 'MÁS POPULAR',
    loading_plans: 'Cargando opciones...',
    val_free_forever: 'Gratis por siempre',
    val_per_user: 'Por Usuario',
    bloatedFields: 'Campos Redundantes',
    totalUnusedFields: 'Total de Nodos Inactivos',
    all: 'Todos los Endpoints',
    searchPlaceholderOpt: 'Buscar por patrón de endpoint o URL...',
    connectionError: 'Pérdida de Telemetría',
    retryConnection: 'Restaurar Conexión',
    networkSilence: 'Sin Señales Activas Detectadas',
    noOptimizationsDesc: 'El motor de optimización está inactivo. Activa el modo de optimización en tu SDK para iniciar la recolección de datos de alta fidelidad.',
    unused: 'Redundante',
    used: 'Funcional',
    totalFields: 'Nodos de Respuesta',
    internalInsights: 'Abrir Inteligencia',
    redundancyAnalysis: 'Análisis de Redundancia',
    bloatFactor: 'Ratio de Redundancia',
    metadataSummary: 'Resumen de Metadatos del Payload',
    sizeKb: 'Tamaño de Transferencia (KB)',
    totalNodes: 'Total de Nodos de Respuesta',
    activeSchemaCoverage: 'Cobertura de Esquema Activa',
    optimizationReport: 'Reporte de Inteligencia de Divergencia',
    optimizationReportDesc: 'Nodos identificados como potencialmente redundantes por nuestro motor heurístico avanzado en uno o más contextos.',
    criticalBloat: 'Redundancia Crítica',
    intelligentRecommendations: 'Insights Cognitivos de Optimización',
    recommendationsDesc: 'Nuestro análisis indica que estos campos no están siendo consumidos por el DOM ni por procedimientos lógicos en el stack actual. Considera el filtrado en backend para reducir la latencia y los costos de ancho de banda.',
    analysisHash: 'Hash de Optimización Global',
    closeInsights: 'Cerrar Inteligencia',
    refreshAnalysis: 'Re-analizar Tráfico',
    efficiency: 'Eficiencia',
    network: 'Red',
    divergenceDetectedDesc: 'Divergencia estructural detectada: este recurso se utiliza de forma diferente en múltiples contextos de la aplicación.',
    performanceConflict: 'Divergencia Estructural',
    globalRedundancy: 'Redundancias Globales',
    removeGlobally: 'Eliminar Globalmente',
    screenComparison: 'Divergencia Contextual',
    extraFields: 'Ahorros Contextuales',
    globalUnusedFields: 'Seguro para Eliminar Globalmente',
    remove: 'Eliminar',
    clearAll: 'Purgar Datos de Optimización',
    confirmClearAll: '¿Estás seguro de que deseas purgar permanentemente toda la inteligencia de optimización para este proyecto? Esta operación es irreversible.',
    noSprintSelected: 'Ningún Sprint Seleccionado',
    selectSprintToView: 'Selecciona un sprint de la barra lateral para ver sus ítems y progreso.',
    pendingPlanning: 'Pendiente de Planificación',
    themePreview: 'Tema Visual',
    live: 'En Vivo'
  }
};

export const LanguageProvider = ({ children }) => {
  const getInitialLanguage = () => {
    const saved = localStorage.getItem('lang');
    if (saved) return saved;

    // Detect browser language
    const browserLang = typeof navigator !== 'undefined' ? navigator.language : 'en';
    const mainLang = (browserLang || 'en').split('-')[0];

    // Only support languages we actually have translations for
    return translations[mainLang] ? mainLang : 'en';
  };

  const [lang, setLang] = useState(getInitialLanguage());

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  const t = (key, options = {}) => {
    let text = translations[lang][key] || key;
    Object.keys(options).forEach(optKey => {
      text = text.replace(`{${optKey}}`, options[optKey]);
    });
    return text;
  };

  const value = React.useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
